<template>
  <div id="reportPage">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-box">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading...</div>
      </div>
    </div>
    <main>
      <article>
        <div class="title-wrapper mb-4">
          <h2 class="login-title">BILLABLE REPORT</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>
        <!-- Weekly transfers stats for user (non-admin) -->
        <div v-if="!isAdmin" class="transfers-stats-panel mb-3">
          <div class="transfers-stats-card">
            <div class="transfers-stats-header">
              <span class="transfers-stats-title">Weekly Order</span>
              <span class="transfers-stats-week">{{ currentWeekLabel }}</span>
            </div>
            <div class="transfers-stats-body">
              <div class="transfers-stats-main">
                <span class="transfers-stats-value">{{ weeklyStats.completed }}</span>
                <span class="transfers-stats-sep">/</span>
                <span class="transfers-stats-total">{{ weeklyStats.total }}</span>
                <span class="transfers-stats-unit">transfers</span>
              </div>
              <div class="transfers-stats-progress-wrap">
                <div class="transfers-stats-progress-bar">
                  <div
                    class="transfers-stats-progress-fill"
                    :style="{ width: Math.min(weeklyStats.percent, 100) + '%' }"
                  ></div>
                </div>
                <span class="transfers-stats-percent">{{ weeklyStats.percentText }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="table-top-filters d-flex justify-content-end mb-2" style="width: 100%;">
            <DateRangePicker v-model="selectedRange" @change="onDateRangeChange" />
            <AutoRefresh v-model="refreshRate" @refresh="fetchData" />
            <a
              href="#"
              class="link-csv"
              :class="{ 'link-csv--disabled': isExportingCsv }"
              @click.prevent="downloadCSV"
            >
              <font-awesome-icon icon="file-csv" />
              {{ isExportingCsv ? 'Preparing CSV…' : 'Download CSV' }}
            </a>
           </div>

        <div class="table-responsive">
          <table class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 200px;">DATE</th>
                <th class="campaign-header-cell" style="width: 170px;">
                  <div class="campaign-header-cell-inner">
                    <span>LEAD PHONE NUMBER</span>
                    <button
                      type="button"
                      ref="callerFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: callerFilterActive }"
                      @click.stop="toggleCallerFilterPopover"
                      title="Filter by lead phone number"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="callerFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th style="width: 100px;">DURATION</th>
                <th style="width: 150px;">CONNECTED DURATION</th>
                <th>TRANSFER NUMBER</th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>RECEIVING AGENCY</span>
                    <button
                      type="button"
                      ref="targetNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: targetNameFilterSelectedCount > 0 }"
                      @click.stop="toggleTargetNameFilterPopover"
                      title="Filter by receiving agency"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="targetNameFilterSelectedCount > 0" class="filter-badge">{{ targetNameFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 160px;">
                  <div class="campaign-header-cell-inner">
                    <span>LEAD SOURCE</span>
                    <button
                      type="button"
                      ref="leadSourceFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: leadSourceFilterSelectedCount > 0 }"
                      @click.stop="toggleLeadSourceFilterPopover"
                      title="Filter by lead source"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="leadSourceFilterSelectedCount > 0" class="filter-badge">{{ leadSourceFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>FIRST NAME</span>
                    <button
                      type="button"
                      ref="firstNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: firstNameFilterActive }"
                      @click.stop="toggleFirstNameFilterPopover"
                      title="Filter by first name"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="firstNameFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 120px;">
                  <div class="campaign-header-cell-inner">
                    <span>LAST NAME</span>
                    <button
                      type="button"
                      ref="lastNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: lastNameFilterActive }"
                      @click.stop="toggleLastNameFilterPopover"
                      title="Filter by last name"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="lastNameFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 110px;">
                  <div class="campaign-header-cell-inner">
                    <span>DOB</span>
                    <button
                      type="button"
                      ref="dobFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: dobFilterActive }"
                      @click.stop="toggleDobFilterPopover"
                      title="Filter by date of birth"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="dobFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 120px;">
                  <div class="campaign-header-cell-inner">
                    <span>EMAIL</span>
                    <button
                      type="button"
                      ref="emailFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: emailFilterActive }"
                      @click.stop="toggleEmailFilterPopover"
                      title="Filter by email"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="emailFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 220px;">
                  <div class="campaign-header-cell-inner">
                    <span>ADDRESS</span>
                    <button
                      type="button"
                      ref="addressFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: addressFilterActive }"
                      @click.stop="toggleAddressFilterPopover"
                      title="Filter by address"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="addressFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 56px;">
                  <div class="campaign-header-cell-inner">
                    <span>STATE</span>
                    <button
                      type="button"
                      ref="stateFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: stateFilterSelectedCount > 0 }"
                      @click.stop="toggleStateFilterPopover"
                      title="Filter by state"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="stateFilterSelectedCount > 0" class="filter-badge">{{ stateFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 70px;">
                  <div class="campaign-header-cell-inner">
                    <span>ZIP</span>
                    <button
                      type="button"
                      ref="zipFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: zipFilterActive }"
                      @click.stop="toggleZipFilterPopover"
                      title="Filter by ZIP"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="zipFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CURRENT INSURANCE</span>
                    <button
                      type="button"
                      ref="currentInsuranceFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: currentInsuranceFilterSelectedCount > 0 }"
                      @click.stop="toggleCurrentInsuranceFilterPopover"
                      title="Filter by current insurance"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="currentInsuranceFilterSelectedCount > 0" class="filter-badge">{{ currentInsuranceFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>INSURED SINCE</span>
                    <button
                      type="button"
                      ref="insuredSinceFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: insuredSinceFilterActive }"
                      @click.stop="toggleInsuredSinceFilterPopover"
                      title="Filter by insured since"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="insuredSinceFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>INSURANCE EXPIRATION</span>
                    <button
                      type="button"
                      ref="insuranceExpirationFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: insuranceExpirationFilterActive }"
                      @click.stop="toggleInsuranceExpirationFilterPopover"
                      title="Filter by insurance expiration"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="insuranceExpirationFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 60px;">
                  <div class="campaign-header-cell-inner">
                    <span>VEHICLES</span>
                    <button
                      type="button"
                      ref="vehiclesFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: vehiclesFilterSelectedCount > 0 }"
                      @click.stop="toggleVehiclesFilterPopover"
                      title="Filter by vehicles"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="vehiclesFilterSelectedCount > 0" class="filter-badge">{{ vehiclesFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 70px;">
                  <div class="campaign-header-cell-inner">
                    <span>YEAR</span>
                    <button
                      type="button"
                      ref="yearFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: yearFilterActive }"
                      @click.stop="toggleYearFilterPopover"
                      title="Filter by year"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="yearFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 100px;">
                  <div class="campaign-header-cell-inner">
                    <span>MAKE</span>
                    <button
                      type="button"
                      ref="makeFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: makeFilterActive }"
                      @click.stop="toggleMakeFilterPopover"
                      title="Filter by make"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="makeFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 120px;">
                  <div class="campaign-header-cell-inner">
                    <span>MODEL</span>
                    <button
                      type="button"
                      ref="modelFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: modelFilterActive }"
                      @click.stop="toggleModelFilterPopover"
                      title="Filter by model"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="modelFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>VIN</span>
                    <button
                      type="button"
                      ref="vinFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: vinFilterActive }"
                      @click.stop="toggleVinFilterPopover"
                      title="Filter by VIN"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="vinFilterActive" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell" style="width: 120px;">
                  <div class="campaign-header-cell-inner">
                    <span>OWN OR RENT</span>
                    <button
                      type="button"
                      ref="ownOrRentFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: ownOrRentFilterSelectedCount > 0 }"
                      @click.stop="toggleOwnOrRentFilterPopover"
                      title="Filter by own or rent"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="ownOrRentFilterSelectedCount > 0" class="filter-badge">{{ ownOrRentFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th style="width: 100px;">HANGUP</th>
                <th>REFUND REQUEST</th>
                <th class="campaign-header-cell" style="width: 100px;">
                  <div class="campaign-header-cell-inner">
                    <span>DISPOSITION</span>
                    <button
                      type="button"
                      ref="dispositionFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: dispositionFilterSelectedCount > 0 }"
                      @click.stop="toggleDispositionFilterPopover"
                      title="Filter by disposition"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="dispositionFilterSelectedCount > 0" class="filter-badge">{{ dispositionFilterSelectedCount }}</span>
                    </button>
                  </div>
                </th>
                <th style="width: 130px;">BILLABLE STATUS</th>
                <th>RECORDING</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in pagedRows" :key="i">
                <td>{{ displayValue(row.callDt) }}</td>
                <td>{{ displayValue(row.inboundPhoneNumber) }}</td>
                <td>{{ displayValue(row.callLengthInSeconds) }}</td>
                <td>{{ displayValue(row.connectedCallLengthInSeconds) }}</td>
                <td>{{ displayValue(row.targetNumber) }}</td>
                <td>{{ displayValue(row.targetName) }}</td>
                <td>{{ displayLeadSource(row) }}</td>
                <td>{{ displayValue(row['tag:User:first_name']) }}</td>
                <td>{{ displayValue(row['tag:User:last_name']) }}</td>
                <td>{{ displayDob(row) }}</td>
                <td>{{ displayValue(row['tag:User:email']) }}</td>
                <td>{{ displayAddress(row) }}</td>
                <td>{{ displayValue(row['tag:User:state']) }}</td>
                <td>{{ displayValue(row['tag:User:zip']) }}</td>
                <td>{{ displayValue(row['tag:User:currentinsurance']) }}</td>
                <td>{{ displayValue(row['tag:User:insured_since']) }}</td>
                <td>{{ displayValue(row['tag:User:insurance_expiration']) }}</td>
                <td>{{ displayValue(row['tag:User:number_of_vehicles']) }}</td>
                <td>{{ displayValue(row['tag:User:year']) }}</td>
                <td>{{ displayValue(row['tag:User:make']) }}</td>
                <td>{{ displayValue(row['tag:User:model']) }}</td>
                <td>{{ displayValue(row['tag:User:vin']) }}</td>
                <td>{{ displayValue(row['tag:User:own_or_rent']) }}</td>
                <td class="text-center">
                  <template v-if="row.endCallSource">
                    <span v-if="row.endCallSource === 'Target'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'location-crosshairs']" />
                      <span class="tooltip-text right">Target</span>
                    </span>
                    <span v-else-if="row.endCallSource === 'System'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'computer']" />
                      <span class="tooltip-text right">System</span>
                    </span>
                    <span v-else-if="row.endCallSource === 'Caller'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'mobile-screen']" />
                      <span class="tooltip-text right">Caller</span>
                    </span>
                    <span v-else>
                      {{ row.endCallSource }}
                    </span>
                  </template>
                </td>
                <td class="text-center">
                  <template v-if="row.refundRequestCreated">
                    <span
                      v-for="refund in row.existingRefunds"
                      :key="refund.id"
                      class="refund-reason-chip"
                      :class="{
                        'refund-chip-approved': refund.is_applied === 1,
                        'refund-chip-rejected': refund.is_applied === 2,
                        'refund-chip-pending': refund.is_applied === 0 || refund.is_applied == null
                      }"
                      @click="openRefundModal(refund, row)"
                      :title="refund.reason"
                    >
                      <span class="refund-chip-status-dot"></span>
                      <span class="refund-chip-text">{{ refund.reason }}</span>
                    </span>
                  </template>

                    <!-- ЕСЛИ refund нет — проверяем можно ли создать -->
                    <a
                      v-else-if="canCreateRefund(row)"
                      href="#"
                      class="coins-action"
                      @click.prevent="openCreateModal(row)"
                    >
                      <font-awesome-icon icon="coins" class="users-icon" />
                    </a>

                </td>

                <td class="text-center">
                  <span v-if="row.portalDisposition">{{ row.portalDisposition }}</span>
                </td>

                <td class="text-center">
                  <div class="billable-status-wrap">
                    <template v-if="isDisplayedBillable(row)">
                      <font-awesome-icon
                        icon="check-circle"
                        style="color: green;"
                      />
                    </template>
                    <template v-else>
                      <font-awesome-icon
                        icon="times-circle"
                        style="color: red;"
                      />
                    </template>
                    <a
                      v-if="canChangeBillable"
                      href="#"
                      class="billable-action-link"
                      @click.prevent="openBillableStatusModal(row)"
                    >
                      Change
                    </a>
                    <button
                      v-if="getBillableOverrideReason(row)"
                      type="button"
                      class="refund-reason-chip billable-reason-chip"
                      :class="getBillableReasonChipClass(row)"
                      @click="openBillableReasonModal(row)"
                    >
                      <span class="refund-chip-status-dot"></span>
                      <span class="refund-chip-text">{{ getBillableOverrideReason(row) }}</span>
                    </button>
                    <div
                      v-if="canChangeBillable && getBillableChangedByWatermark(row)"
                      class="billable-watermark"
                      :title="getBillableChangedByWatermark(row)"
                    >
                      {{ getBillableChangedByWatermark(row) }}
                    </div>
                  </div>
                </td>
                
                <td class="text-center">
                  <div v-if="row.recordingUrl" class="d-flex align-items-center justify-content-center">
                    <a @click="downloadRecording(row)" class="audio-btn download-btn ms-2">
                      <font-awesome-icon icon="download" />
                    </a>
                    <button class="audio-btn play-btn ms-2" @click="toggleAudio(i, row)">
                      <font-awesome-icon v-if="!audioPlaying[i]" icon="play" />
                      <font-awesome-icon v-else icon="pause" />
                    </button>
                    <audio
                      :id="`audio-${row.id || i}`"
                      :src="row.recordingUrl"
                      preload="none"
                      @ended="audioEnded(i)"
                      @error="audioError(i)"
                      style="display: none;"
                    >
                    </audio>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="18" class="text-center py-3">No results</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            ◀ Prev
          </button>

          <span>
            Page {{ currentPage }} of {{ totalPages }}
          </span>

          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage >= totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next ▶
          </button>
        </div>

        <button class="btn-submit mt-3" @click="clearFilters">Clear filters</button>
      </article>

      <!-- Caller filter popover (like Phone in Home.vue) -->
      <Teleport to="body">
        <div
          v-if="showCallerFilterModal"
          ref="callerFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="callerFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Lead Phone Number</span>
            <button type="button" class="filter-popover-close" @click="closeCallerFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="callerFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="Lead phone number (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearCallerFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyCallerFilter">Apply</button>
          </div>
        </div>
        <!-- Target Name filter popover -->
        <div
          v-if="showTargetNameFilterModal"
          ref="targetNameFilterPopover"
          class="filter-popover"
          :style="targetNameFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Receiving Agency</span>
            <button type="button" class="filter-popover-close" @click="closeTargetNameFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="targetNameFilterSearch"
              type="text"
              class="filter-popover-search"
              placeholder="Search receiving agency..."
            />
            <div class="filter-popover-list">
              <div v-if="targetNameOptionsLoading" class="filter-popover-empty">
                Loading agencies…
              </div>
              <template v-else>
                <label
                  v-for="opt in filteredTargetNameOptions"
                  :key="opt"
                  class="filter-popover-item"
                >
                  <input
                    type="checkbox"
                    :value="opt"
                    v-model="targetNameFilterSelected"
                  />
                  <span>{{ displayTargetNameOption(opt) }}</span>
                </label>
                <div v-if="filteredTargetNameOptions.length === 0" class="filter-popover-empty">
                  No options match
                </div>
              </template>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllTargetNames">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllTargetNames">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyTargetNameFilter">Apply</button>
          </div>
        </div>
        <!-- First Name filter popover -->
        <div
          v-if="showFirstNameFilterModal"
          ref="firstNameFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="firstNameFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by First Name</span>
            <button type="button" class="filter-popover-close" @click="closeFirstNameFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="firstNameFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="First name (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearFirstNameFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyFirstNameFilter">Apply</button>
          </div>
        </div>
        <!-- Last Name filter popover -->
        <div
          v-if="showLastNameFilterModal"
          ref="lastNameFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="lastNameFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Last Name</span>
            <button type="button" class="filter-popover-close" @click="closeLastNameFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="lastNameFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="Last name (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearLastNameFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyLastNameFilter">Apply</button>
          </div>
        </div>
        <!-- Current Insurance filter popover (like Campaign in Home.vue) -->
        <div
          v-if="showCurrentInsuranceFilterModal"
          ref="currentInsuranceFilterPopover"
          class="filter-popover"
          :style="currentInsuranceFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Current Insurance</span>
            <button type="button" class="filter-popover-close" @click="closeCurrentInsuranceFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="currentInsuranceFilterSearch"
              type="text"
              class="filter-popover-search"
              placeholder="Search (contains)..."
            />
            <div class="filter-popover-list">
              <label
                v-for="opt in filteredCurrentInsuranceOptions"
                :key="opt"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt"
                  v-model="currentInsuranceFilterSelected"
                />
                <span>{{ opt }}</span>
              </label>
              <div v-if="filteredCurrentInsuranceOptions.length === 0" class="filter-popover-empty">
                No options match
              </div>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllCurrentInsurance">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllCurrentInsurance">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyCurrentInsuranceFilter">Apply</button>
          </div>
        </div>
        <!-- State filter popover (like Current Insurance) -->
        <div
          v-if="showStateFilterModal"
          ref="stateFilterPopover"
          class="filter-popover"
          :style="stateFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by State</span>
            <button type="button" class="filter-popover-close" @click="closeStateFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="stateFilterSearch"
              type="text"
              class="filter-popover-search"
              placeholder="Search (contains)..."
            />
            <div class="filter-popover-list">
              <label
                v-for="opt in filteredStateOptions"
                :key="opt"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt"
                  v-model="stateFilterSelected"
                />
                <span>{{ opt }}</span>
              </label>
              <div v-if="filteredStateOptions.length === 0" class="filter-popover-empty">
                No options match
              </div>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllState">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllState">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyStateFilter">Apply</button>
          </div>
        </div>
        <!-- Own or Rent filter popover (like State) -->
        <div
          v-if="showOwnOrRentFilterModal"
          ref="ownOrRentFilterPopover"
          class="filter-popover"
          :style="ownOrRentFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Own or Rent</span>
            <button type="button" class="filter-popover-close" @click="closeOwnOrRentFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="ownOrRentFilterSearch"
              type="text"
              class="filter-popover-search"
              placeholder="Search (contains)..."
            />
            <div class="filter-popover-list">
              <label
                v-for="opt in filteredOwnOrRentOptions"
                :key="opt"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt"
                  v-model="ownOrRentFilterSelected"
                />
                <span>{{ opt }}</span>
              </label>
              <div v-if="filteredOwnOrRentOptions.length === 0" class="filter-popover-empty">
                No options match
              </div>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllOwnOrRent">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllOwnOrRent">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyOwnOrRentFilter">Apply</button>
          </div>
        </div>
        <!-- Vehicles filter popover (like Own or Rent) -->
        <div
          v-if="showVehiclesFilterModal"
          ref="vehiclesFilterPopover"
          class="filter-popover"
          :style="vehiclesFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Vehicles</span>
            <button type="button" class="filter-popover-close" @click="closeVehiclesFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="vehiclesFilterSearch"
              type="text"
              class="filter-popover-search"
              placeholder="Search (contains)..."
            />
            <div class="filter-popover-list">
              <label
                v-for="opt in filteredVehiclesOptions"
                :key="opt"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt"
                  v-model="vehiclesFilterSelected"
                />
                <span>{{ opt }}</span>
              </label>
              <div v-if="filteredVehiclesOptions.length === 0" class="filter-popover-empty">
                No options match
              </div>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllVehicles">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllVehicles">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyVehiclesFilter">Apply</button>
          </div>
        </div>
        <!-- Disposition filter -->
        <div
          v-if="showDispositionFilterModal"
          ref="dispositionFilterPopover"
          class="filter-popover"
          :style="dispositionFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Disposition</span>
            <button type="button" class="filter-popover-close" @click="closeDispositionFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <div class="filter-popover-list">
              <label
                v-for="opt in dispositionOptions"
                :key="opt"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt"
                  v-model="dispositionFilterSelected"
                />
                <span>{{ opt }}</span>
              </label>
            </div>
            <div class="filter-popover-hint">Filter by supported portal disposition codes.</div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllDisposition">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllDisposition">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyDispositionFilter">Apply</button>
          </div>
        </div>
        <!-- Billable Status filter (Billable / Non-billable) -->
        <div
          v-if="showBillableStatusFilterModal"
          ref="billableStatusFilterPopover"
          class="filter-popover"
          :style="billableStatusFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Billable Status</span>
            <button type="button" class="filter-popover-close" @click="closeBillableStatusFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <div class="filter-popover-list">
              <label
                v-for="opt in billableStatusOptions"
                :key="opt.value"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt.value"
                  v-model="billableStatusFilterSelected"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllBillableStatus">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllBillableStatus">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyBillableStatusFilter">Apply</button>
          </div>
        </div>
        <!-- Lead Source filter popover -->
        <div
          v-if="showLeadSourceFilterModal"
          ref="leadSourceFilterPopover"
          class="filter-popover"
          :style="leadSourceFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Lead Source</span>
            <button type="button" class="filter-popover-close" @click="closeLeadSourceFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <div class="filter-popover-list">
              <label
                v-for="opt in leadSourceOptions"
                :key="opt.value"
                class="filter-popover-item"
              >
                <input
                  type="checkbox"
                  :value="opt.value"
                  v-model="leadSourceFilterSelected"
                />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllLeadSource">All</button>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="deselectAllLeadSource">None</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyLeadSourceFilter">Apply</button>
          </div>
        </div>
        <!-- DOB filter popover -->
        <div
          v-if="showDobFilterModal"
          ref="dobFilterPopover"
          class="filter-popover caller-filter-popover dob-filter-popover"
          :style="dobFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by DOB</span>
            <button type="button" class="filter-popover-close" @click="closeDobFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body dob-filter-body" lang="en-US">
            <p class="filter-popover-hint">Подпись поля (дд.мм.гг) — от браузера/ОС. В фильтр уходит дата в формате YYYY-MM-DD.</p>
            <label class="dob-filter-label">Exact date</label>
            <input
              v-model="dobFilterExactTemp"
              type="date"
              lang="en-US"
              class="filter-popover-search"
            />
            <label class="dob-filter-label">From</label>
            <input
              v-model="dobFilterFromTemp"
              type="date"
              lang="en-US"
              class="filter-popover-search"
            />
            <label class="dob-filter-label">To</label>
            <input
              v-model="dobFilterToTemp"
              type="date"
              lang="en-US"
              class="filter-popover-search"
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearDobFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyDobFilter">Apply</button>
          </div>
        </div>
        <!-- Email filter popover -->
        <div
          v-if="showEmailFilterModal"
          ref="emailFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="emailFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Email</span>
            <button type="button" class="filter-popover-close" @click="closeEmailFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="emailFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="Email (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearEmailFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyEmailFilter">Apply</button>
          </div>
        </div>
        <!-- Address filter popover -->
        <div
          v-if="showAddressFilterModal"
          ref="addressFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="addressFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Address</span>
            <button type="button" class="filter-popover-close" @click="closeAddressFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="addressFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="Address (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearAddressFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyAddressFilter">Apply</button>
          </div>
        </div>
        <!-- Year filter popover -->
        <div
          v-if="showYearFilterModal"
          ref="yearFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="yearFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Year</span>
            <button type="button" class="filter-popover-close" @click="closeYearFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="yearFilterTemp" type="text" class="filter-popover-search" placeholder="Year (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearYearFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyYearFilter">Apply</button>
          </div>
        </div>
        <!-- Make filter popover -->
        <div
          v-if="showMakeFilterModal"
          ref="makeFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="makeFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Make</span>
            <button type="button" class="filter-popover-close" @click="closeMakeFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="makeFilterTemp" type="text" class="filter-popover-search" placeholder="Make (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearMakeFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyMakeFilter">Apply</button>
          </div>
        </div>
        <!-- Insured Since filter popover -->
        <div
          v-if="showInsuredSinceFilterModal"
          ref="insuredSinceFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="insuredSinceFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Insured Since</span>
            <button type="button" class="filter-popover-close" @click="closeInsuredSinceFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="insuredSinceFilterTemp" type="text" class="filter-popover-search" placeholder="Insured Since (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearInsuredSinceFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyInsuredSinceFilter">Apply</button>
          </div>
        </div>
        <!-- Insurance Expiration filter popover -->
        <div
          v-if="showInsuranceExpirationFilterModal"
          ref="insuranceExpirationFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="insuranceExpirationFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Insurance Expiration</span>
            <button type="button" class="filter-popover-close" @click="closeInsuranceExpirationFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="insuranceExpirationFilterTemp" type="text" class="filter-popover-search" placeholder="Insurance Expiration (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearInsuranceExpirationFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyInsuranceExpirationFilter">Apply</button>
          </div>
        </div>
        <!-- VIN filter popover -->
        <div
          v-if="showVinFilterModal"
          ref="vinFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="vinFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by VIN</span>
            <button type="button" class="filter-popover-close" @click="closeVinFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="vinFilterTemp" type="text" class="filter-popover-search" placeholder="VIN (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearVinFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyVinFilter">Apply</button>
          </div>
        </div>
        <!-- Model filter popover -->
        <div
          v-if="showModelFilterModal"
          ref="modelFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="modelFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Model</span>
            <button type="button" class="filter-popover-close" @click="closeModelFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input v-model="modelFilterTemp" type="text" class="filter-popover-search" placeholder="Model (contains)..." />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearModelFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyModelFilter">Apply</button>
          </div>
        </div>
        <!-- ZIP filter popover (like Caller) -->
        <div
          v-if="showZipFilterModal"
          ref="zipFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="zipFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by ZIP</span>
            <button type="button" class="filter-popover-close" @click="closeZipFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="zipFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="ZIP (contains)..."
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearZipFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyZipFilter">Apply</button>
          </div>
        </div>
      </Teleport>

      <div v-if="showCreateModal" class="modal-overday">
        <div class="modal-create-content" @click.stop>
          <h2 class="modal-title">Return reason</h2>

          <form @submit.prevent="submitReturnReason" class="modal-form">

            <textarea
              v-model="refundReason"
              placeholder="Enter return reason..."
              required
              class="large-textarea"
              :readonly="isViewMode"
            ></textarea>

            <div class="modal-actions mt-3">
              <button 
                type="button" class="btn btn-secondary" @click="closeCreateModal">
                Cancel
              </button>
              <button
                v-if="!isViewMode"
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting">Submitting…</span>
                <span v-else>Submit</span>
              </button>
            </div>

          </form>
        </div>
      </div>

      <div v-if="showBillableStatusModal" class="modal-overday" @click="closeBillableStatusModal">
        <div class="modal-create-content billable-status-modal" @click.stop>
          <h2 class="modal-title">Change Billable Status</h2>

          <div v-if="billableStatusRow" class="billable-status-modal-body">
            <div><strong>Lead Phone Number:</strong> {{ displayValue(billableStatusRow.inboundPhoneNumber) || '—' }}</div>
            <div><strong>Transfer Number:</strong> {{ displayValue(billableStatusRow.targetNumber) || '—' }}</div>
            <div><strong>Current status:</strong> {{ getDisplayedBillableLabel(billableStatusRow) }}</div>
            <label class="billable-reason-label">
              <strong>Reason:</strong>
              <select
                v-model="billableStatusReasonChoice"
                class="form-select mt-2"
                :disabled="billableStatusSubmitting"
              >
                <option value="" disabled>Select a reason</option>
                <option value="TNC">TNC</option>
                <option v-if="showThuBillableReason" value="THU">THU (Target Hang up)</option>
                <option value="Refunded">Refunded</option>
                <option value="Other">Other</option>
              </select>
              <textarea
                v-if="billableStatusReasonChoice === 'Other'"
                v-model="billableStatusReasonOther"
                class="form-control mt-2 billable-reason-other"
                rows="4"
                maxlength="255"
                placeholder="Enter reason"
                :disabled="billableStatusSubmitting"
              ></textarea>
            </label>
          </div>

          <div class="modal-actions mt-3">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeBillableStatusModal"
              :disabled="billableStatusSubmitting"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="submitBillableStatusChange('non_billable')"
              :disabled="billableStatusSubmitting || !billableStatusCanSubmit || billableStatusReasonChoice === 'THU'"
            >
              Mark Non-billable
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="submitBillableStatusChange('billable')"
              :disabled="billableStatusSubmitting || !billableStatusCanSubmit"
            >
              Mark Billable
            </button>
          </div>
        </div>
      </div>

      <div v-if="showBillableReasonModal" class="modal-overday" @click="closeBillableReasonModal">
        <div class="modal-create-content" @click.stop>
          <h2 class="modal-title">Billable status reason</h2>

          <textarea
            :value="billableReasonViewText"
            class="large-textarea"
            readonly
          ></textarea>

          <div class="modal-actions mt-3">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeBillableReasonModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue'
import UserMenu from '@/components/UserMenu.vue'
import AutoRefresh from '@/components/AutoRefresh.vue'

export default {
  name: "BillableReport",
  components: { DateRangePicker, UserMenu, AutoRefresh },
  data() {
    return {
      transferNumber: null,
      transferNumbers: [],
      selectedRange: null,
      rows: [],
      allRows: [],
      refreshRate: 0,
      isExportingCsv: false,
      creatingRow: null,
      refundReason: '',
      showRefundModal: false,
      selectedRow: null,
      showCreateModal: false,
      showBillableStatusModal: false,
      showBillableReasonModal: false,
      billableStatusRow: null,
      billableReasonViewText: "",
      billableStatusReasonChoice: "",
      billableStatusReasonOther: "",
      billableStatusSubmitting: false,
      isSubmitting: false,
      summary: {},
      campaignOptions: [],
      insuranceOptions: [],
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 100,
      isLoading: false,
      billableThresholdSeconds: 60,
      userName: localStorage.getItem("username") || "User",
      roles: [],
      audioPlaying: {},
      showCallerFilterModal: false,
      callerFilterTemp: "",
      callerFilterPopoverStyle: {},
      callerFilterClickOutside: null,
      showTargetNameFilterModal: false,
      targetNameFilterSelected: [],
      targetNameFilterSearch: "",
      targetNameFilterPopoverStyle: {},
      targetNameFilterClickOutside: null,
      targetNameOptions: [],
      targetNameOptionsLoading: false,
      showCurrentInsuranceFilterModal: false,
      currentInsuranceFilterSearch: "",
      currentInsuranceFilterSelected: [],
      currentInsuranceFilterPopoverStyle: {},
      currentInsuranceFilterClickOutside: null,
      showZipFilterModal: false,
      zipFilterTemp: "",
      zipFilterPopoverStyle: {},
      zipFilterClickOutside: null,
      showFirstNameFilterModal: false,
      firstNameFilterTemp: "",
      firstNameFilterPopoverStyle: {},
      firstNameFilterClickOutside: null,
      showLastNameFilterModal: false,
      lastNameFilterTemp: "",
      lastNameFilterPopoverStyle: {},
      lastNameFilterClickOutside: null,
      showDobFilterModal: false,
      dobFilterExactTemp: "",
      dobFilterFromTemp: "",
      dobFilterToTemp: "",
      dobFilterPopoverStyle: {},
      dobFilterClickOutside: null,
      showEmailFilterModal: false,
      emailFilterTemp: "",
      emailFilterPopoverStyle: {},
      emailFilterClickOutside: null,
      showAddressFilterModal: false,
      addressFilterTemp: "",
      addressFilterPopoverStyle: {},
      addressFilterClickOutside: null,
      showYearFilterModal: false,
      yearFilterTemp: "",
      yearFilterPopoverStyle: {},
      yearFilterClickOutside: null,
      showMakeFilterModal: false,
      makeFilterTemp: "",
      makeFilterPopoverStyle: {},
      makeFilterClickOutside: null,
      showInsuredSinceFilterModal: false,
      insuredSinceFilterTemp: "",
      insuredSinceFilterPopoverStyle: {},
      insuredSinceFilterClickOutside: null,
      showInsuranceExpirationFilterModal: false,
      insuranceExpirationFilterTemp: "",
      insuranceExpirationFilterPopoverStyle: {},
      insuranceExpirationFilterClickOutside: null,
      showVinFilterModal: false,
      vinFilterTemp: "",
      vinFilterPopoverStyle: {},
      vinFilterClickOutside: null,
      showModelFilterModal: false,
      modelFilterTemp: "",
      modelFilterPopoverStyle: {},
      modelFilterClickOutside: null,
      showStateFilterModal: false,
      stateFilterSearch: "",
      stateFilterSelected: [],
      stateFilterPopoverStyle: {},
      stateFilterClickOutside: null,
      showOwnOrRentFilterModal: false,
      ownOrRentFilterSearch: "",
      ownOrRentFilterSelected: [],
      ownOrRentFilterPopoverStyle: {},
      ownOrRentFilterClickOutside: null,
      showVehiclesFilterModal: false,
      vehiclesFilterSearch: "",
      vehiclesFilterSelected: [],
      vehiclesFilterPopoverStyle: {},
      vehiclesFilterClickOutside: null,
      showDispositionFilterModal: false,
      dispositionFilterSelected: [],
      dispositionFilterPopoverStyle: {},
      dispositionFilterClickOutside: null,
      dispositionOptions: [
        "TNC", "TSHU", "THU", "XFER",
        "DNQAGE", "DNQAS", "DNQLC", "DNQSF", "DNQWS", "NOCON", "NOCOV",
      ],
      showBillableStatusFilterModal: false,
      billableStatusFilterSelected: ["billable"],
      billableStatusFilterPopoverStyle: {},
      billableStatusFilterClickOutside: null,
      billableStatusOptions: [
        { value: "billable", label: "Billable" },
        { value: "non_billable", label: "Non-billable" },
      ],
      showLeadSourceFilterModal: false,
      leadSourceFilterSelected: [],
      leadSourceFilterPopoverStyle: {},
      leadSourceFilterClickOutside: null,
      leadSourceOptions: [
        { value: "Outbound Qualified", label: "Outbound Qualified" },
        { value: "Verified Opt-In", label: "Verified Opt-In" },
      ],
      filters: {
        date_from: "",
        date_to: "",
        inboundPhoneNumber: "",
        targetNumber: "",
        targetName: [],
        currentinsurance: [],
        zip: "",
        firstname: "",
        lastname: "",
        dob: "",
        dob_from: "",
        dob_to: "",
        email: "",
        address: "",
        state: [],
        own_or_rent: [],
        number_of_vehicles: [],
        year: "",
        make: "",
        model: "",
        insured_since: "",
        insurance_expiration: "",
        vin: "",
        disposition: [],
        billable_status: ["billable"],
        lead_source: [],
        duration: "",
        connected_duration: "",
        endCallSource: ""
      },
      weeklyStats: {
        completed: 0,
        total: 0,
        percent: 0,
        percentText: "0%"
      }
    };
  },
  computed: {
    isAdmin() {
      return Array.isArray(this.roles) && this.roles.includes("admin");
    },
    isManager() {
      return Array.isArray(this.roles) && this.roles.includes("manager");
    },
    canChangeBillable() {
      // Managers should be able to mark calls billable/non-billable.
      return this.isAdmin || this.isManager;
    },
    showThuBillableReason() {
      if (this.billableStatusReasonChoice === "THU") return true;
      return Boolean(this.billableStatusRow) && !this.isDisplayedBillable(this.billableStatusRow);
    },
    billableStatusCanSubmit() {
      if (
        this.billableStatusReasonChoice === "TNC" ||
        this.billableStatusReasonChoice === "THU" ||
        this.billableStatusReasonChoice === "Refunded"
      ) {
        return true;
      }
      if (this.billableStatusReasonChoice === "Other") {
        return Boolean((this.billableStatusReasonOther || "").trim());
      }
      return false;
    },
    currentWeekLabel() {
      if (!this.filters.date_from) return "";
      const d = new Date(this.filters.date_from);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(d);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const fmt = (x) => x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `${fmt(start)} – ${fmt(end)}`;
    },
    filteredRows() {
      return this.rows;
    },
    pagedRows() {
      return this.rows;
    },
    callerFilterActive() {
      return (this.filters.inboundPhoneNumber || "").toString().trim() !== "";
    },
    targetNameFilterSelectedCount() {
      return Array.isArray(this.filters.targetName) ? this.filters.targetName.length : 0;
    },
    filteredTargetNameOptions() {
      const q = (this.targetNameFilterSearch || "").trim().toLowerCase();
      if (!q) return this.targetNameOptions || [];
      return (this.targetNameOptions || []).filter((opt) =>
        this.displayTargetNameOption(opt).toLowerCase().includes(q)
      );
    },
    currentInsuranceOptions() {
      const seen = new Set();
      for (const row of this.allRows || []) {
        const v = row['tag:User:currentinsurance'];
        const s = String(v ?? '').trim();
        if (s !== "") seen.add(s);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    },
    filteredCurrentInsuranceOptions() {
      const q = (this.currentInsuranceFilterSearch || "").trim().toLowerCase();
      if (!q) return this.currentInsuranceOptions || [];
      return (this.currentInsuranceOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    currentInsuranceFilterSelectedCount() {
      return Array.isArray(this.filters.currentinsurance) ? this.filters.currentinsurance.length : 0;
    },
    stateOptions() {
      const seen = new Set();
      for (const row of this.allRows || []) {
        const v = row['tag:User:state'];
        const s = String(v ?? '').trim();
        if (s !== "") seen.add(s);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    },
    filteredStateOptions() {
      const q = (this.stateFilterSearch || "").trim().toLowerCase();
      if (!q) return this.stateOptions || [];
      return (this.stateOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    stateFilterSelectedCount() {
      return Array.isArray(this.filters.state) ? this.filters.state.length : 0;
    },
    leadSourceFilterSelectedCount() {
      return Array.isArray(this.filters.lead_source) ? this.filters.lead_source.length : 0;
    },
    ownOrRentOptions() {
      const seen = new Set();
      for (const row of this.allRows || []) {
        const v = row['tag:User:own_or_rent'];
        const s = String(v ?? '').trim();
        if (s !== "") seen.add(s);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    },
    filteredOwnOrRentOptions() {
      const q = (this.ownOrRentFilterSearch || "").trim().toLowerCase();
      if (!q) return this.ownOrRentOptions || [];
      return (this.ownOrRentOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    ownOrRentFilterSelectedCount() {
      return Array.isArray(this.filters.own_or_rent) ? this.filters.own_or_rent.length : 0;
    },
    vehiclesOptions() {
      const seen = new Set();
      for (const row of this.allRows || []) {
        const v = row['tag:User:number_of_vehicles'];
        const s = String(v ?? '').trim();
        if (s !== "") seen.add(s);
      }
      return [...seen].sort((a, b) => a.localeCompare(b));
    },
    filteredVehiclesOptions() {
      const q = (this.vehiclesFilterSearch || "").trim().toLowerCase();
      if (!q) return this.vehiclesOptions || [];
      return (this.vehiclesOptions || []).filter((opt) =>
        String(opt || "").toLowerCase().includes(q)
      );
    },
    vehiclesFilterSelectedCount() {
      return Array.isArray(this.filters.number_of_vehicles) ? this.filters.number_of_vehicles.length : 0;
    },
    dispositionFilterSelectedCount() {
      return Array.isArray(this.filters.disposition) ? this.filters.disposition.length : 0;
    },
    billableStatusFilterSelectedCount() {
      return Array.isArray(this.filters.billable_status) ? this.filters.billable_status.length : 0;
    },
    zipFilterActive() {
      return (this.filters.zip || "").toString().trim() !== "";
    },
    firstNameFilterActive() {
      return (this.filters.firstname || "").toString().trim() !== "";
    },
    lastNameFilterActive() {
      return (this.filters.lastname || "").toString().trim() !== "";
    },
    dobFilterActive() {
      return (
        (this.filters.dob || "").toString().trim() !== "" ||
        (this.filters.dob_from || "").toString().trim() !== "" ||
        (this.filters.dob_to || "").toString().trim() !== ""
      );
    },
    emailFilterActive() {
      return (this.filters.email || "").toString().trim() !== "";
    },
    addressFilterActive() {
      return (this.filters.address || "").toString().trim() !== "";
    },
    yearFilterActive() {
      return (this.filters.year || "").toString().trim() !== "";
    },
    makeFilterActive() {
      return (this.filters.make || "").toString().trim() !== "";
    },
    insuredSinceFilterActive() {
      return (this.filters.insured_since || "").toString().trim() !== "";
    },
    insuranceExpirationFilterActive() {
      return (this.filters.insurance_expiration || "").toString().trim() !== "";
    },
    vinFilterActive() {
      return (this.filters.vin || "").toString().trim() !== "";
    },
    modelFilterActive() {
      return (this.filters.model || "").toString().trim() !== "";
    }
  },
  mounted() {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    
    this.check_role()
      .then(() => {
        // Managers should not be restricted by transferNumber/targetNumber filtering.
        const roles = Array.isArray(this.roles) ? this.roles : [];
        if (roles.includes("admin") || roles.includes("manager")) return;
        return this.fetchTransferNumber();
      })
      .then(() => {
        return this.fetchCampaigns();
      })
      .then(() => {
        return this.fetchBillableSettings();
      })
      .then(() => {
        const getNow = () => {
          const nyStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
          return new Date(nyStr);
        };
        const now = getNow();
        const fmt = (d) => {
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} `
            + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        };
        const from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const to   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        this.selectedRange = [from, to];
        this.filters.date_from = fmt(from);
        this.filters.date_to   = fmt(to);
        return this.fetchData();
      })
      .then(() => {
        document.title = "Billable Report";
      })
      .catch((error) => {
        console.error("loading error", error);
      });
  },
  beforeUnmount() {
    this.removeCallerFilterClickOutside();
    this.removeTargetNameFilterClickOutside();
    this.removeCurrentInsuranceFilterClickOutside();
    this.removeZipFilterClickOutside();
    this.removeFirstNameFilterClickOutside();
    this.removeLastNameFilterClickOutside();
    this.removeDobFilterClickOutside();
    this.removeEmailFilterClickOutside();
    this.removeAddressFilterClickOutside();
    this.removeYearFilterClickOutside();
    this.removeMakeFilterClickOutside();
    this.removeInsuredSinceFilterClickOutside();
    this.removeInsuranceExpirationFilterClickOutside();
    this.removeVinFilterClickOutside();
    this.removeModelFilterClickOutside();
    this.removeStateFilterClickOutside();
    this.removeOwnOrRentFilterClickOutside();
    this.removeVehiclesFilterClickOutside();
    this.removeDispositionFilterClickOutside();
    this.removeBillableStatusFilterClickOutside();
    this.removeLeadSourceFilterClickOutside();
  },
  methods: {
    onDateRangeChange({ dateFrom, dateTo }) {
      this.filters.date_from = dateFrom;
      this.filters.date_to   = dateTo;
      this.currentPage = 1;
      this.fetchData();
    },
    hasManualBillableOverride(row) {
      return typeof row?.billableOverride?.isBillable === "boolean";
    },
    /** Per-row threshold from API (owner of targetNumber) or logged-in user's setting from fetchBillableSettings. */
    effectiveBillableThresholdSeconds(row) {
      const v = row?.billableThresholdSeconds;
      if (v != null && v !== "" && Number.isFinite(Number(v))) {
        return Number(v);
      }
      return Number(this.billableThresholdSeconds || 60);
    },
    isDisplayedBillable(row) {
      if (this.hasManualBillableOverride(row)) {
        return row.billableOverride.isBillable;
      }

      const hasApprovedRefund = row?.existingRefunds && row.existingRefunds.some(r => r.is_applied === 1);
      if (hasApprovedRefund) return false;
      const thr = this.effectiveBillableThresholdSeconds(row);
      return Number(row?.connectedCallLengthInSeconds || 0) > thr;
    },
    getDisplayedBillableLabel(row) {
      return this.isDisplayedBillable(row) ? "Billable" : "Non-billable";
    },
    getBillableOverrideReason(row) {
      if (!this.hasManualBillableOverride(row)) return "";
      return String(row?.billableOverride?.reason || "").trim();
    },
    getBillableReasonChipClass(row) {
      return this.isDisplayedBillable(row) ? "refund-chip-approved" : "refund-chip-rejected";
    },
    formatBillableChangedAt(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getBillableChangedByWatermark(row) {
      if (!this.hasManualBillableOverride(row)) return "";
      const by = String(row?.billableOverride?.updatedBy || "").trim();
      if (!by) return "";
      const at = this.formatBillableChangedAt(row?.billableOverride?.updatedAt);
      return at ? `by ${by} · ${at}` : `by ${by}`;
    },
    getAutoBillableLabel(row) {
      const hasApprovedRefund = row?.existingRefunds && row.existingRefunds.some(r => r.is_applied === 1);
      if (hasApprovedRefund) return 'Non-billable';
      const thr = this.effectiveBillableThresholdSeconds(row);
      return Number(row?.connectedCallLengthInSeconds || 0) > thr ? 'Billable' : 'Non-billable';
    },
    async fetchBillableSettings() {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch('/api/settings/billable', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        this.billableThresholdSeconds = Number(data.thresholdSeconds || 60);
      } catch (e) {
        console.error("Failed to fetch billable settings:", e);
      }
    },
    openBillableStatusModal(row) {
      this.billableStatusRow = row;
      const reason = String(row?.billableOverride?.reason || "").trim();
      const normalized = reason.toUpperCase();
      if (normalized === "TNC") {
        this.billableStatusReasonChoice = "TNC";
        this.billableStatusReasonOther = "";
      } else if (normalized === "THU" || normalized.startsWith("THU (")) {
        this.billableStatusReasonChoice = "THU";
        this.billableStatusReasonOther = "";
      } else if (normalized === "REFUNDED") {
        this.billableStatusReasonChoice = "Refunded";
        this.billableStatusReasonOther = "";
      } else if (reason) {
        this.billableStatusReasonChoice = "Other";
        this.billableStatusReasonOther = reason;
      } else {
        this.billableStatusReasonChoice = "";
        this.billableStatusReasonOther = "";
      }
      this.showBillableStatusModal = true;
    },
    openBillableReasonModal(row) {
      this.billableReasonViewText = this.getBillableOverrideReason(row);
      this.showBillableReasonModal = true;
    },
    closeBillableReasonModal() {
      this.showBillableReasonModal = false;
      this.billableReasonViewText = "";
    },
    closeBillableStatusModal() {
      this.showBillableStatusModal = false;
      this.billableStatusRow = null;
      this.billableStatusReasonChoice = "";
      this.billableStatusReasonOther = "";
      this.billableStatusSubmitting = false;
    },
    resolvedBillableStatusReason() {
      if (
        this.billableStatusReasonChoice === "TNC" ||
        this.billableStatusReasonChoice === "THU" ||
        this.billableStatusReasonChoice === "Refunded"
      ) {
        return this.billableStatusReasonChoice;
      }
      if (this.billableStatusReasonChoice === "Other") {
        return (this.billableStatusReasonOther || "").trim();
      }
      return "";
    },
    async submitBillableStatusChange(status) {
      if (!this.billableStatusRow?.inboundCallId || this.billableStatusSubmitting || !this.billableStatusCanSubmit) return;
      const reason = this.resolvedBillableStatusReason();
      if (!reason) return;

      this.billableStatusSubmitting = true;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("/api/calllogs/billable_override", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            inboundCallId: this.billableStatusRow.inboundCallId,
            status,
            reason
          })
        });

        if (!res.ok) {
          throw new Error("Failed to update billable status");
        }

        const data = await res.json();
        const nextOverride = {
          id: data.id,
          inboundCallId: data.inboundCallId,
          isBillable: Boolean(data.isBillable),
          reason: data.reason,
          updatedBy: data.updatedBy,
          updatedAt: data.updatedAt
        };

        const applyOverride = (list) => {
          const row = list.find(item => item.inboundCallId === this.billableStatusRow.inboundCallId);
          if (row) {
            row.billableOverride = nextOverride;
            const reasonToken = String(nextOverride.reason || "").trim().toUpperCase();
            if (reasonToken === "TNC") {
              row.portalDisposition = "TNC";
            } else if (reasonToken === "THU") {
              row.portalDisposition = "THU";
            }
          }
        };

        applyOverride(this.rows);
        applyOverride(this.allRows);
        this.closeBillableStatusModal();
      } catch (e) {
        console.error("Billable status request error:", e);
      } finally {
        this.billableStatusSubmitting = false;
      }
    },
    canCreateRefund(row) {
      if (!row.callDt) return false;

      if (this.hasManualBillableOverride(row) && !row.billableOverride.isBillable) {
        return false;
      }

      const thr = this.effectiveBillableThresholdSeconds(row);
      if (row.connectedCallLengthInSeconds == null || row.connectedCallLengthInSeconds === '' || Number(row.connectedCallLengthInSeconds) <= thr) return false;

      if (Number(row.connectedCallLengthInSeconds) > 300) {
        return false;
      }

      const nyNowStr = new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York'
      });
      const nyNow = new Date(nyNowStr);

      const callDate = new Date(row.callDt);

      const diffMs = nyNow - callDate;

      const twelveHoursMs = 12 * 60 * 60 * 1000;

      if (diffMs > twelveHoursMs) {
        return false;
      }

      return true;
    },
    openRefundModal(refund, row) {
      this.creatingRow = row;
      this.editingRefundId = refund.id;
      this.refundReason = refund.reason;

      this.isViewMode = refund.is_applied !== 0;

      this.showCreateModal = true;
    }
  ,closeRefundModal() {
    this.showRefundModal = false;
    this.selectedRow = null;
  },
  openEditModal(refund, row) {
    this.creatingRow = row;
    this.refundReason = refund.reason;
    this.showCreateModal = true;
    this.editingRefundId = refund.id;
  },
  openCreateModal(row) {
    if (this.hasManualBillableOverride(row) && !row.billableOverride.isBillable) {
      return;
    }
    this.creatingRow = row;
    this.refundReason='';
    this.showCreateModal = true;
    console.log(this.showCreateModal)
    
  },
  async submitReturnReason() {
    if (!this.refundReason || !this.creatingRow) return;
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const token = localStorage.getItem("token");
    const payload = {
      reason: this.refundReason.trim(),
      row: this.creatingRow,
      username: localStorage.getItem("username")
    };

    let url = "/api/refund_requests";
    let method = "POST";

    if (this.editingRefundId) {
      url = `/api/refund_requests/${this.editingRefundId}`;
      method = "PUT";
    }

    try {
      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Failed to create/update refund request");

      const data = await res.json();
      console.log("Refund saved:", data);

      const rowIndex = this.allRows.indexOf(this.creatingRow);
      if (rowIndex !== -1) {
        const updatedRow = {
          ...this.creatingRow,
          refundRequestCreated: true,
          existingRefunds: this.editingRefundId
            ? this.creatingRow.existingRefunds.map(r => r.id === this.editingRefundId ? data : r)
            : [data]
        };
        this.allRows.splice(rowIndex, 1, updatedRow);
      }

      this.refundReason = "";
      this.creatingRow = null;
      this.editingRefundId = null;
      this.showCreateModal = false;

    } catch (err) {
      console.error(err);
    } finally {
      this.isSubmitting = false;
    }
  },

  async submitRefundRequest() {
    if (!this.refundReason) {
      alert("Please enter a reason for the refund request.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/refund-request", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          rowId: this.selectedRow.id, // Идентификатор строки
          reason: this.refundReason // Причина запроса
        })
      });

      if (!response.ok) {
        throw new Error("Failed to submit refund request");
      }

      alert("Refund request submitted successfully");
      this.closeRefundModal(); // Закрываем модальное окно
    } catch (e) {
      console.error("Error submitting refund request:", e);
      alert("An error occurred while submitting the refund request.");
    }
  },
     formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    // Получаем YYYY-MM-DD HH:mm:ss
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  },
  async fetchTransferNumber() {
    const token = localStorage.getItem("token");
    const username = this.userName;

    try {
      const res = await fetch(`/api/clients/${username}/transfer_numbers`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error("Failed to fetch transfer number");

      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items.filter(Boolean) : [];
      this.transferNumbers = items;
      this.transferNumber = items[0] || null;
      console.log("transfernumbers", this.transferNumbers)
    } catch (e) {
      console.error("Error fetching transfer number:", e);
    }
  },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchData();
    },
    async fetchTargetNameOptions() {
      const token = localStorage.getItem("token");
      if (!token) return;

      const built = this.buildCalllogsRequestData(1, 1);
      if (built === null) {
        this.targetNameOptions = [];
        return;
      }

      const { requestData } = built;
      const payload = {
        reportStart: requestData.reportStart,
        reportEnd: requestData.reportEnd,
      };
      if (requestData.targetNumbers) {
        payload.targetNumbers = requestData.targetNumbers;
      } else if (requestData.targetNumber) {
        payload.targetNumber = requestData.targetNumber;
      }

      this.targetNameOptionsLoading = true;
      try {
        const res = await fetch("/api/calllogs/target_names", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          this.targetNameOptions = [];
          return;
        }
        const data = await res.json();
        this.targetNameOptions = (data.items || []).filter(Boolean);
      } catch (e) {
        console.error("Error fetching target names:", e);
        this.targetNameOptions = [];
      } finally {
        this.targetNameOptionsLoading = false;
      }
    },
    async fetchCampaigns() {
      const token = localStorage.getItem("token");
      const username = this.userName; // ты уже его используешь

      const res = await fetch(
        `/api/clients/${username}/campaigns`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await res.json();
      this.campaignOptions = data.items || [];
    },

  displayValue(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return value;
  },
  displayTargetNameOption(opt) {
    return opt === "__EMPTY__" ? "(Empty)" : opt;
  },
  displayDob(row) {
    const value = row['tag:User:DOB'] ?? row['tag:User:dob'];
    const displayed = this.displayValue(value);
    if (!displayed) return '';
    const s = String(displayed).trim();
    if (s === '0000-00-00' || s === '0001-01-01') return '';
    return displayed;
  },
  displayAddress(row) {
    const value = row['tag:User:address'] ?? row['tag:User:full_address'];
    return this.displayValue(value);
  },
  displayLeadSource(row) {
    const map = {
      "9am - 7pm All Clients": "Outbound Qualified",
      "Opt In Data TEST": "Verified Opt-In",
    };
    const raw = row?.campaignName;
    if (raw === null || raw === undefined) return '';
    const key = String(raw).trim();
    if (!key) return '';
    return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : '';
  },

  async downloadRecording(row) {
  try {
    const res = await this.authorizedFetch(`/api/proxyDownload?url=${encodeURIComponent(row.recordingUrl)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    if (!res.ok) throw new Error("Ошибка загрузки");

    const blob = await res.blob();
    const fileName = row.recordingUrl.split("/").pop().split("?")[0] || "recording.mp3";

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Ошибка скачивания аудио:", e);
    alert("Не удалось скачать аудио. Возможно CORS или недоступная ссылка.");
  }
},
        
    async toggleAudio(i, row) {
      const audioId = `audio-${row.id || i}`;
      const audio = document.getElementById(audioId);

      if (!audio) return;

      if (this.audioPlaying[i]) {
        audio.pause();
        this.audioPlaying[i] = false;
      } else {
        // Останавливаем все другие треки
        Object.keys(this.audioPlaying).forEach(key => {
          if (this.audioPlaying[key] && key !== String(i)) {
            const otherRow = this.rows[parseInt(key)];
            if (otherRow) {
              const otherAudio = document.getElementById(`audio-${otherRow.id || key}`);
              if (otherAudio) {
                otherAudio.pause();
                this.audioPlaying[key] = false;
              }
            }
          }
        });

        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("token")
          },
          body: JSON.stringify({
            action_type: `play_recording ringba ${row.id}`,
            username: localStorage.getItem("username")
          })
        });

        audio.play().catch(err => {
          console.error("Error playing audio:", err);
          this.audioPlaying[i] = false;
        });
        this.audioPlaying[i] = true;
      }
    },
    audioEnded(i) {
      this.audioPlaying[i] = false;
    },
    audioError(i) {
      this.audioPlaying[i] = false;
    },
    async check_role() {
      const token = localStorage.getItem('token');
      const username = localStorage.getItem('username');
      const params = new URLSearchParams({ user: username });

      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401) {
        const refresh_token = localStorage.getItem("refresh_token");
        if (!refresh_token) {
          this.logout();
          return;
        }

        // Запрос на рефреш токена
        const refreshRes = await fetch("/api/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token })
        });

        if (!refreshRes.ok) {
          this.logout();
          return;
        }

        const data = await refreshRes.json();
        let token  = data.token;
        localStorage.setItem("token", token);

        let res = await fetch(`/api/role?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      const data = await res.json();
      this.roles = data.roles;
      console.log("roles")
      console.log(this.roles)
      return this.roles;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.creatingRow = null;
    },
    toggleCallerFilterPopover() {
      if (this.showCallerFilterModal) {
        this.closeCallerFilterModal();
      } else {
        this.openCallerFilterModal();
      }
    },
    openCallerFilterModal() {
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.callerFilterTemp = this.filters.inboundPhoneNumber || "";
      this.showCallerFilterModal = true;
      this.$nextTick(() => this.positionCallerFilterPopover());
      this.addCallerFilterClickOutside();
    },
    closeCallerFilterModal() {
      this.showCallerFilterModal = false;
      this.removeCallerFilterClickOutside();
    },
    positionCallerFilterPopover() {
      const btn = this.$refs.callerFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.callerFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyCallerFilter() {
      this.filters.inboundPhoneNumber = (this.callerFilterTemp || "").trim();
      this.closeCallerFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearCallerFilter() {
      this.callerFilterTemp = "";
      this.filters.inboundPhoneNumber = "";
      this.closeCallerFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addCallerFilterClickOutside() {
      this.removeCallerFilterClickOutside();
      setTimeout(() => {
        this.callerFilterClickOutside = (e) => {
          const popover = this.$refs.callerFilterPopover;
          const btn = this.$refs.callerFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeCallerFilterModal();
        };
        document.addEventListener("click", this.callerFilterClickOutside);
      }, 0);
    },
    removeCallerFilterClickOutside() {
      if (this.callerFilterClickOutside) {
        document.removeEventListener("click", this.callerFilterClickOutside);
        this.callerFilterClickOutside = null;
      }
    },
    toggleTargetNameFilterPopover() {
      if (this.showTargetNameFilterModal) {
        this.closeTargetNameFilterModal();
      } else {
        this.openTargetNameFilterModal();
      }
    },
    async openTargetNameFilterModal() {
      this.closeCallerFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.targetNameFilterSelected = Array.isArray(this.filters.targetName)
        ? [...this.filters.targetName]
        : this.filters.targetName
          ? [this.filters.targetName]
          : [];
      this.targetNameFilterSearch = "";
      this.showTargetNameFilterModal = true;
      this.$nextTick(() => this.positionTargetNameFilterPopover());
      this.addTargetNameFilterClickOutside();
      await this.fetchTargetNameOptions();
    },
    closeTargetNameFilterModal() {
      this.showTargetNameFilterModal = false;
      this.removeTargetNameFilterClickOutside();
    },
    positionTargetNameFilterPopover() {
      const btn = this.$refs.targetNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.targetNameFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyTargetNameFilter() {
      this.filters.targetName = [...(this.targetNameFilterSelected || [])];
      this.closeTargetNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllTargetNames() {
      this.targetNameFilterSelected = [...this.filteredTargetNameOptions];
    },
    deselectAllTargetNames() {
      this.targetNameFilterSelected = [];
    },
    clearTargetNameFilter() {
      this.targetNameFilterSelected = [];
      this.filters.targetName = [];
      this.closeTargetNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addTargetNameFilterClickOutside() {
      this.removeTargetNameFilterClickOutside();
      setTimeout(() => {
        this.targetNameFilterClickOutside = (e) => {
          const popover = this.$refs.targetNameFilterPopover;
          const btn = this.$refs.targetNameFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeTargetNameFilterModal();
        };
        document.addEventListener("click", this.targetNameFilterClickOutside);
      }, 0);
    },
    removeTargetNameFilterClickOutside() {
      if (this.targetNameFilterClickOutside) {
        document.removeEventListener("click", this.targetNameFilterClickOutside);
        this.targetNameFilterClickOutside = null;
      }
    },
    toggleFirstNameFilterPopover() {
      if (this.showFirstNameFilterModal) {
        this.closeFirstNameFilterModal();
      } else {
        this.openFirstNameFilterModal();
      }
    },
    openFirstNameFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.firstNameFilterTemp = this.filters.firstname || "";
      this.showFirstNameFilterModal = true;
      this.$nextTick(() => this.positionFirstNameFilterPopover());
      this.addFirstNameFilterClickOutside();
    },
    closeFirstNameFilterModal() {
      this.showFirstNameFilterModal = false;
      this.removeFirstNameFilterClickOutside();
    },
    positionFirstNameFilterPopover() {
      const btn = this.$refs.firstNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.firstNameFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyFirstNameFilter() {
      this.filters.firstname = (this.firstNameFilterTemp || "").trim();
      this.closeFirstNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearFirstNameFilter() {
      this.firstNameFilterTemp = "";
      this.filters.firstname = "";
      this.closeFirstNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addFirstNameFilterClickOutside() {
      this.removeFirstNameFilterClickOutside();
      setTimeout(() => {
        this.firstNameFilterClickOutside = (e) => {
          const popover = this.$refs.firstNameFilterPopover;
          const btn = this.$refs.firstNameFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeFirstNameFilterModal();
        };
        document.addEventListener("click", this.firstNameFilterClickOutside);
      }, 0);
    },
    removeFirstNameFilterClickOutside() {
      if (this.firstNameFilterClickOutside) {
        document.removeEventListener("click", this.firstNameFilterClickOutside);
        this.firstNameFilterClickOutside = null;
      }
    },
    toggleLastNameFilterPopover() {
      if (this.showLastNameFilterModal) {
        this.closeLastNameFilterModal();
      } else {
        this.openLastNameFilterModal();
      }
    },
    openLastNameFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeFirstNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.lastNameFilterTemp = this.filters.lastname || "";
      this.showLastNameFilterModal = true;
      this.$nextTick(() => this.positionLastNameFilterPopover());
      this.addLastNameFilterClickOutside();
    },
    closeLastNameFilterModal() {
      this.showLastNameFilterModal = false;
      this.removeLastNameFilterClickOutside();
    },
    positionLastNameFilterPopover() {
      const btn = this.$refs.lastNameFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.lastNameFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyLastNameFilter() {
      this.filters.lastname = (this.lastNameFilterTemp || "").trim();
      this.closeLastNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearLastNameFilter() {
      this.lastNameFilterTemp = "";
      this.filters.lastname = "";
      this.closeLastNameFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addLastNameFilterClickOutside() {
      this.removeLastNameFilterClickOutside();
      setTimeout(() => {
        this.lastNameFilterClickOutside = (e) => {
          const popover = this.$refs.lastNameFilterPopover;
          const btn = this.$refs.lastNameFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeLastNameFilterModal();
        };
        document.addEventListener("click", this.lastNameFilterClickOutside);
      }, 0);
    },
    removeLastNameFilterClickOutside() {
      if (this.lastNameFilterClickOutside) {
        document.removeEventListener("click", this.lastNameFilterClickOutside);
        this.lastNameFilterClickOutside = null;
      }
    },
    toggleCurrentInsuranceFilterPopover() {
      if (this.showCurrentInsuranceFilterModal) {
        this.closeCurrentInsuranceFilterModal();
      } else {
        this.openCurrentInsuranceFilterModal();
      }
    },
    openCurrentInsuranceFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.currentInsuranceFilterSelected = Array.isArray(this.filters.currentinsurance)
        ? [...this.filters.currentinsurance]
        : this.filters.currentinsurance ? [this.filters.currentinsurance] : [];
      this.currentInsuranceFilterSearch = "";
      this.showCurrentInsuranceFilterModal = true;
      this.$nextTick(() => this.positionCurrentInsuranceFilterPopover());
      this.addCurrentInsuranceFilterClickOutside();
    },
    closeCurrentInsuranceFilterModal() {
      this.showCurrentInsuranceFilterModal = false;
      this.removeCurrentInsuranceFilterClickOutside();
    },
    positionCurrentInsuranceFilterPopover() {
      const btn = this.$refs.currentInsuranceFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.currentInsuranceFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyCurrentInsuranceFilter() {
      this.filters.currentinsurance = [...this.currentInsuranceFilterSelected];
      this.closeCurrentInsuranceFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllCurrentInsurance() {
      this.currentInsuranceFilterSelected = [...this.filteredCurrentInsuranceOptions];
    },
    deselectAllCurrentInsurance() {
      this.currentInsuranceFilterSelected = [];
    },
    addCurrentInsuranceFilterClickOutside() {
      this.removeCurrentInsuranceFilterClickOutside();
      setTimeout(() => {
        this.currentInsuranceFilterClickOutside = (e) => {
          const popover = this.$refs.currentInsuranceFilterPopover;
          const btn = this.$refs.currentInsuranceFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeCurrentInsuranceFilterModal();
        };
        document.addEventListener("click", this.currentInsuranceFilterClickOutside);
      }, 0);
    },
    removeCurrentInsuranceFilterClickOutside() {
      if (this.currentInsuranceFilterClickOutside) {
        document.removeEventListener("click", this.currentInsuranceFilterClickOutside);
        this.currentInsuranceFilterClickOutside = null;
      }
    },
    toggleStateFilterPopover() {
      if (this.showStateFilterModal) {
        this.closeStateFilterModal();
      } else {
        this.openStateFilterModal();
      }
    },
    openStateFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.stateFilterSelected = Array.isArray(this.filters.state)
        ? [...this.filters.state]
        : this.filters.state ? [this.filters.state] : [];
      this.stateFilterSearch = "";
      this.showStateFilterModal = true;
      this.$nextTick(() => this.positionStateFilterPopover());
      this.addStateFilterClickOutside();
    },
    closeStateFilterModal() {
      this.showStateFilterModal = false;
      this.removeStateFilterClickOutside();
    },
    positionStateFilterPopover() {
      const btn = this.$refs.stateFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.stateFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyStateFilter() {
      this.filters.state = [...this.stateFilterSelected];
      this.closeStateFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllState() {
      this.stateFilterSelected = [...this.filteredStateOptions];
    },
    deselectAllState() {
      this.stateFilterSelected = [];
    },
    addStateFilterClickOutside() {
      this.removeStateFilterClickOutside();
      setTimeout(() => {
        this.stateFilterClickOutside = (e) => {
          const popover = this.$refs.stateFilterPopover;
          const btn = this.$refs.stateFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeStateFilterModal();
        };
        document.addEventListener("click", this.stateFilterClickOutside);
      }, 0);
    },
    removeStateFilterClickOutside() {
      if (this.stateFilterClickOutside) {
        document.removeEventListener("click", this.stateFilterClickOutside);
        this.stateFilterClickOutside = null;
      }
    },
    toggleOwnOrRentFilterPopover() {
      if (this.showOwnOrRentFilterModal) {
        this.closeOwnOrRentFilterModal();
      } else {
        this.openOwnOrRentFilterModal();
      }
    },
    openOwnOrRentFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.ownOrRentFilterSelected = Array.isArray(this.filters.own_or_rent)
        ? [...this.filters.own_or_rent]
        : this.filters.own_or_rent ? [this.filters.own_or_rent] : [];
      this.ownOrRentFilterSearch = "";
      this.showOwnOrRentFilterModal = true;
      this.$nextTick(() => this.positionOwnOrRentFilterPopover());
      this.addOwnOrRentFilterClickOutside();
    },
    closeOwnOrRentFilterModal() {
      this.showOwnOrRentFilterModal = false;
      this.removeOwnOrRentFilterClickOutside();
    },
    positionOwnOrRentFilterPopover() {
      const btn = this.$refs.ownOrRentFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.ownOrRentFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyOwnOrRentFilter() {
      this.filters.own_or_rent = [...this.ownOrRentFilterSelected];
      this.closeOwnOrRentFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllOwnOrRent() {
      this.ownOrRentFilterSelected = [...this.filteredOwnOrRentOptions];
    },
    deselectAllOwnOrRent() {
      this.ownOrRentFilterSelected = [];
    },
    addOwnOrRentFilterClickOutside() {
      this.removeOwnOrRentFilterClickOutside();
      setTimeout(() => {
        this.ownOrRentFilterClickOutside = (e) => {
          const popover = this.$refs.ownOrRentFilterPopover;
          const btn = this.$refs.ownOrRentFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeOwnOrRentFilterModal();
        };
        document.addEventListener("click", this.ownOrRentFilterClickOutside);
      }, 0);
    },
    removeOwnOrRentFilterClickOutside() {
      if (this.ownOrRentFilterClickOutside) {
        document.removeEventListener("click", this.ownOrRentFilterClickOutside);
        this.ownOrRentFilterClickOutside = null;
      }
    },
    toggleVehiclesFilterPopover() {
      if (this.showVehiclesFilterModal) {
        this.closeVehiclesFilterModal();
      } else {
        this.openVehiclesFilterModal();
      }
    },
    openVehiclesFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeDispositionFilterModal();
      this.vehiclesFilterSelected = Array.isArray(this.filters.number_of_vehicles)
        ? [...this.filters.number_of_vehicles]
        : this.filters.number_of_vehicles ? [this.filters.number_of_vehicles] : [];
      this.vehiclesFilterSearch = "";
      this.showVehiclesFilterModal = true;
      this.$nextTick(() => this.positionVehiclesFilterPopover());
      this.addVehiclesFilterClickOutside();
    },
    closeVehiclesFilterModal() {
      this.showVehiclesFilterModal = false;
      this.removeVehiclesFilterClickOutside();
    },
    positionVehiclesFilterPopover() {
      const btn = this.$refs.vehiclesFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.vehiclesFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyVehiclesFilter() {
      this.filters.number_of_vehicles = [...this.vehiclesFilterSelected];
      this.closeVehiclesFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllVehicles() {
      this.vehiclesFilterSelected = [...this.filteredVehiclesOptions];
    },
    deselectAllVehicles() {
      this.vehiclesFilterSelected = [];
    },
    addVehiclesFilterClickOutside() {
      this.removeVehiclesFilterClickOutside();
      setTimeout(() => {
        this.vehiclesFilterClickOutside = (e) => {
          const popover = this.$refs.vehiclesFilterPopover;
          const btn = this.$refs.vehiclesFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeVehiclesFilterModal();
        };
        document.addEventListener("click", this.vehiclesFilterClickOutside);
      }, 0);
    },
    removeVehiclesFilterClickOutside() {
      if (this.vehiclesFilterClickOutside) {
        document.removeEventListener("click", this.vehiclesFilterClickOutside);
        this.vehiclesFilterClickOutside = null;
      }
    },
    toggleDispositionFilterPopover() {
      if (this.showDispositionFilterModal) {
        this.closeDispositionFilterModal();
      } else {
        this.openDispositionFilterModal();
      }
    },
    openDispositionFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeBillableStatusFilterModal();

      this.dispositionFilterSelected = Array.isArray(this.filters.disposition)
        ? [...this.filters.disposition]
        : this.filters.disposition
          ? [this.filters.disposition]
          : [];

      this.showDispositionFilterModal = true;
      this.$nextTick(() => this.positionDispositionFilterPopover());
      this.addDispositionFilterClickOutside();
    },
    closeDispositionFilterModal() {
      this.showDispositionFilterModal = false;
      this.removeDispositionFilterClickOutside();
    },
    positionDispositionFilterPopover() {
      const btn = this.$refs.dispositionFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 320;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.dispositionFilterPopoverStyle = {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: "9999"
      };
    },
    applyDispositionFilter() {
      this.filters.disposition = [...(this.dispositionFilterSelected || [])];
      this.closeDispositionFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllDisposition() {
      this.dispositionFilterSelected = [...(this.dispositionOptions || [])];
    },
    deselectAllDisposition() {
      this.dispositionFilterSelected = [];
    },
    addDispositionFilterClickOutside() {
      this.removeDispositionFilterClickOutside();
      setTimeout(() => {
        this.dispositionFilterClickOutside = (e) => {
          const popover = this.$refs.dispositionFilterPopover;
          const btn = this.$refs.dispositionFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeDispositionFilterModal();
        };
        document.addEventListener("click", this.dispositionFilterClickOutside);
      }, 0);
    },
    removeDispositionFilterClickOutside() {
      if (this.dispositionFilterClickOutside) {
        document.removeEventListener("click", this.dispositionFilterClickOutside);
        this.dispositionFilterClickOutside = null;
      }
    },
    toggleBillableStatusFilterPopover() {
      if (this.showBillableStatusFilterModal) {
        this.closeBillableStatusFilterModal();
      } else {
        this.openBillableStatusFilterModal();
      }
    },
    openBillableStatusFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeZipFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();

      this.billableStatusFilterSelected = Array.isArray(this.filters.billable_status)
        ? [...this.filters.billable_status]
        : this.filters.billable_status
          ? [this.filters.billable_status]
          : [];

      this.showBillableStatusFilterModal = true;
      this.$nextTick(() => this.positionBillableStatusFilterPopover());
      this.addBillableStatusFilterClickOutside();
    },
    closeBillableStatusFilterModal() {
      this.showBillableStatusFilterModal = false;
      this.removeBillableStatusFilterClickOutside();
    },
    positionBillableStatusFilterPopover() {
      const btn = this.$refs.billableStatusFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 300;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.billableStatusFilterPopoverStyle = {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: "9999"
      };
    },
    applyBillableStatusFilter() {
      this.filters.billable_status = [...(this.billableStatusFilterSelected || [])];
      this.closeBillableStatusFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllBillableStatus() {
      this.billableStatusFilterSelected = (this.billableStatusOptions || []).map(o => o.value);
    },
    deselectAllBillableStatus() {
      this.billableStatusFilterSelected = [];
    },
    addBillableStatusFilterClickOutside() {
      this.removeBillableStatusFilterClickOutside();
      setTimeout(() => {
        this.billableStatusFilterClickOutside = (e) => {
          const popover = this.$refs.billableStatusFilterPopover;
          const btn = this.$refs.billableStatusFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeBillableStatusFilterModal();
        };
        document.addEventListener("click", this.billableStatusFilterClickOutside);
      }, 0);
    },
    removeBillableStatusFilterClickOutside() {
      if (this.billableStatusFilterClickOutside) {
        document.removeEventListener("click", this.billableStatusFilterClickOutside);
        this.billableStatusFilterClickOutside = null;
      }
    },
    toggleLeadSourceFilterPopover() {
      if (this.showLeadSourceFilterModal) {
        this.closeLeadSourceFilterModal();
      } else {
        this.openLeadSourceFilterModal();
      }
    },
    openLeadSourceFilterModal() {
      this.leadSourceFilterSelected = Array.isArray(this.filters.lead_source)
        ? [...this.filters.lead_source]
        : this.filters.lead_source
          ? [this.filters.lead_source]
          : [];
      this.showLeadSourceFilterModal = true;
      this.$nextTick(() => this.positionLeadSourceFilterPopover());
      this.addLeadSourceFilterClickOutside();
    },
    closeLeadSourceFilterModal() {
      this.showLeadSourceFilterModal = false;
      this.removeLeadSourceFilterClickOutside();
    },
    positionLeadSourceFilterPopover() {
      const btn = this.$refs.leadSourceFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 300;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.leadSourceFilterPopoverStyle = {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: "9999"
      };
    },
    applyLeadSourceFilter() {
      this.filters.lead_source = [...(this.leadSourceFilterSelected || [])];
      this.closeLeadSourceFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    selectAllLeadSource() {
      this.leadSourceFilterSelected = (this.leadSourceOptions || []).map(o => o.value);
    },
    deselectAllLeadSource() {
      this.leadSourceFilterSelected = [];
    },
    addLeadSourceFilterClickOutside() {
      this.removeLeadSourceFilterClickOutside();
      setTimeout(() => {
        this.leadSourceFilterClickOutside = (e) => {
          const popover = this.$refs.leadSourceFilterPopover;
          const btn = this.$refs.leadSourceFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeLeadSourceFilterModal();
        };
        document.addEventListener("click", this.leadSourceFilterClickOutside);
      }, 0);
    },
    removeLeadSourceFilterClickOutside() {
      if (this.leadSourceFilterClickOutside) {
        document.removeEventListener("click", this.leadSourceFilterClickOutside);
        this.leadSourceFilterClickOutside = null;
      }
    },
    toggleDobFilterPopover() {
      if (this.showDobFilterModal) {
        this.closeDobFilterModal();
      } else {
        this.openDobFilterModal();
      }
    },
    openDobFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.dobFilterExactTemp = this.filters.dob || "";
      this.dobFilterFromTemp = this.filters.dob_from || "";
      this.dobFilterToTemp = this.filters.dob_to || "";
      this.showDobFilterModal = true;
      this.$nextTick(() => this.positionDobFilterPopover());
      this.addDobFilterClickOutside();
    },
    closeDobFilterModal() {
      this.showDobFilterModal = false;
      this.removeDobFilterClickOutside();
    },
    positionDobFilterPopover() {
      const btn = this.$refs.dobFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.dobFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyDobFilter() {
      this.filters.dob = (this.dobFilterExactTemp || "").trim();
      this.filters.dob_from = (this.dobFilterFromTemp || "").trim();
      this.filters.dob_to = (this.dobFilterToTemp || "").trim();
      this.closeDobFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearDobFilter() {
      this.dobFilterExactTemp = "";
      this.dobFilterFromTemp = "";
      this.dobFilterToTemp = "";
      this.filters.dob = "";
      this.filters.dob_from = "";
      this.filters.dob_to = "";
      this.closeDobFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addDobFilterClickOutside() {
      this.removeDobFilterClickOutside();
      setTimeout(() => {
        this.dobFilterClickOutside = (e) => {
          const popover = this.$refs.dobFilterPopover;
          const btn = this.$refs.dobFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeDobFilterModal();
        };
        document.addEventListener("click", this.dobFilterClickOutside);
      }, 0);
    },
    removeDobFilterClickOutside() {
      if (this.dobFilterClickOutside) {
        document.removeEventListener("click", this.dobFilterClickOutside);
        this.dobFilterClickOutside = null;
      }
    },
    toggleEmailFilterPopover() {
      if (this.showEmailFilterModal) {
        this.closeEmailFilterModal();
      } else {
        this.openEmailFilterModal();
      }
    },
    openEmailFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeAddressFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.emailFilterTemp = this.filters.email || "";
      this.showEmailFilterModal = true;
      this.$nextTick(() => this.positionEmailFilterPopover());
      this.addEmailFilterClickOutside();
    },
    closeEmailFilterModal() {
      this.showEmailFilterModal = false;
      this.removeEmailFilterClickOutside();
    },
    positionEmailFilterPopover() {
      const btn = this.$refs.emailFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.emailFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyEmailFilter() {
      this.filters.email = (this.emailFilterTemp || "").trim();
      this.closeEmailFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearEmailFilter() {
      this.emailFilterTemp = "";
      this.filters.email = "";
      this.closeEmailFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addEmailFilterClickOutside() {
      this.removeEmailFilterClickOutside();
      setTimeout(() => {
        this.emailFilterClickOutside = (e) => {
          const popover = this.$refs.emailFilterPopover;
          const btn = this.$refs.emailFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeEmailFilterModal();
        };
        document.addEventListener("click", this.emailFilterClickOutside);
      }, 0);
    },
    removeEmailFilterClickOutside() {
      if (this.emailFilterClickOutside) {
        document.removeEventListener("click", this.emailFilterClickOutside);
        this.emailFilterClickOutside = null;
      }
    },
    toggleAddressFilterPopover() {
      if (this.showAddressFilterModal) {
        this.closeAddressFilterModal();
      } else {
        this.openAddressFilterModal();
      }
    },
    openAddressFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.addressFilterTemp = this.filters.address || "";
      this.showAddressFilterModal = true;
      this.$nextTick(() => this.positionAddressFilterPopover());
      this.addAddressFilterClickOutside();
    },
    closeAddressFilterModal() {
      this.showAddressFilterModal = false;
      this.removeAddressFilterClickOutside();
    },
    positionAddressFilterPopover() {
      const btn = this.$refs.addressFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 300;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.addressFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyAddressFilter() {
      this.filters.address = (this.addressFilterTemp || "").trim();
      this.closeAddressFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearAddressFilter() {
      this.addressFilterTemp = "";
      this.filters.address = "";
      this.closeAddressFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addAddressFilterClickOutside() {
      this.removeAddressFilterClickOutside();
      setTimeout(() => {
        this.addressFilterClickOutside = (e) => {
          const popover = this.$refs.addressFilterPopover;
          const btn = this.$refs.addressFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeAddressFilterModal();
        };
        document.addEventListener("click", this.addressFilterClickOutside);
      }, 0);
    },
    removeAddressFilterClickOutside() {
      if (this.addressFilterClickOutside) {
        document.removeEventListener("click", this.addressFilterClickOutside);
        this.addressFilterClickOutside = null;
      }
    },
    closeYearMakeModelFilters() {
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
    },
    toggleYearFilterPopover() {
      if (this.showYearFilterModal) this.closeYearFilterModal();
      else this.openYearFilterModal();
    },
    openYearFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.yearFilterTemp = this.filters.year || "";
      this.showYearFilterModal = true;
      this.$nextTick(() => this.positionYearFilterPopover());
      this.addYearFilterClickOutside();
    },
    closeYearFilterModal() {
      this.showYearFilterModal = false;
      this.removeYearFilterClickOutside();
    },
    positionYearFilterPopover() {
      this.positionTextFilterPopover("yearFilterBtn", "yearFilterPopoverStyle", 200);
    },
    applyYearFilter() {
      this.filters.year = (this.yearFilterTemp || "").trim();
      this.closeYearFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearYearFilter() {
      this.yearFilterTemp = "";
      this.filters.year = "";
      this.closeYearFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addYearFilterClickOutside() {
      this.addTextFilterClickOutside("yearFilterPopover", "yearFilterBtn", "yearFilterClickOutside", () => this.closeYearFilterModal());
    },
    removeYearFilterClickOutside() {
      this.removeTextFilterClickOutside("yearFilterClickOutside");
    },
    toggleMakeFilterPopover() {
      if (this.showMakeFilterModal) this.closeMakeFilterModal();
      else this.openMakeFilterModal();
    },
    openMakeFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeModelFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.makeFilterTemp = this.filters.make || "";
      this.showMakeFilterModal = true;
      this.$nextTick(() => this.positionMakeFilterPopover());
      this.addMakeFilterClickOutside();
    },
    closeMakeFilterModal() {
      this.showMakeFilterModal = false;
      this.removeMakeFilterClickOutside();
    },
    positionMakeFilterPopover() {
      this.positionTextFilterPopover("makeFilterBtn", "makeFilterPopoverStyle", 240);
    },
    applyMakeFilter() {
      this.filters.make = (this.makeFilterTemp || "").trim();
      this.closeMakeFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearMakeFilter() {
      this.makeFilterTemp = "";
      this.filters.make = "";
      this.closeMakeFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addMakeFilterClickOutside() {
      this.addTextFilterClickOutside("makeFilterPopover", "makeFilterBtn", "makeFilterClickOutside", () => this.closeMakeFilterModal());
    },
    removeMakeFilterClickOutside() {
      this.removeTextFilterClickOutside("makeFilterClickOutside");
    },
    toggleInsuredSinceFilterPopover() {
      if (this.showInsuredSinceFilterModal) this.closeInsuredSinceFilterModal();
      else this.openInsuredSinceFilterModal();
    },
    openInsuredSinceFilterModal() {
      this.insuredSinceFilterTemp = this.filters.insured_since || "";
      this.showInsuredSinceFilterModal = true;
      this.$nextTick(() => this.positionInsuredSinceFilterPopover());
      this.addInsuredSinceFilterClickOutside();
    },
    closeInsuredSinceFilterModal() {
      this.showInsuredSinceFilterModal = false;
      this.removeInsuredSinceFilterClickOutside();
    },
    positionInsuredSinceFilterPopover() {
      this.positionTextFilterPopover("insuredSinceFilterBtn", "insuredSinceFilterPopoverStyle", 240);
    },
    applyInsuredSinceFilter() {
      this.filters.insured_since = (this.insuredSinceFilterTemp || "").trim();
      this.closeInsuredSinceFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearInsuredSinceFilter() {
      this.insuredSinceFilterTemp = "";
      this.filters.insured_since = "";
      this.closeInsuredSinceFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addInsuredSinceFilterClickOutside() {
      this.addTextFilterClickOutside("insuredSinceFilterPopover", "insuredSinceFilterBtn", "insuredSinceFilterClickOutside", () => this.closeInsuredSinceFilterModal());
    },
    removeInsuredSinceFilterClickOutside() {
      this.removeTextFilterClickOutside("insuredSinceFilterClickOutside");
    },
    toggleInsuranceExpirationFilterPopover() {
      if (this.showInsuranceExpirationFilterModal) this.closeInsuranceExpirationFilterModal();
      else this.openInsuranceExpirationFilterModal();
    },
    openInsuranceExpirationFilterModal() {
      this.insuranceExpirationFilterTemp = this.filters.insurance_expiration || "";
      this.showInsuranceExpirationFilterModal = true;
      this.$nextTick(() => this.positionInsuranceExpirationFilterPopover());
      this.addInsuranceExpirationFilterClickOutside();
    },
    closeInsuranceExpirationFilterModal() {
      this.showInsuranceExpirationFilterModal = false;
      this.removeInsuranceExpirationFilterClickOutside();
    },
    positionInsuranceExpirationFilterPopover() {
      this.positionTextFilterPopover("insuranceExpirationFilterBtn", "insuranceExpirationFilterPopoverStyle", 260);
    },
    applyInsuranceExpirationFilter() {
      this.filters.insurance_expiration = (this.insuranceExpirationFilterTemp || "").trim();
      this.closeInsuranceExpirationFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearInsuranceExpirationFilter() {
      this.insuranceExpirationFilterTemp = "";
      this.filters.insurance_expiration = "";
      this.closeInsuranceExpirationFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addInsuranceExpirationFilterClickOutside() {
      this.addTextFilterClickOutside("insuranceExpirationFilterPopover", "insuranceExpirationFilterBtn", "insuranceExpirationFilterClickOutside", () => this.closeInsuranceExpirationFilterModal());
    },
    removeInsuranceExpirationFilterClickOutside() {
      this.removeTextFilterClickOutside("insuranceExpirationFilterClickOutside");
    },
    toggleVinFilterPopover() {
      if (this.showVinFilterModal) this.closeVinFilterModal();
      else this.openVinFilterModal();
    },
    openVinFilterModal() {
      this.vinFilterTemp = this.filters.vin || "";
      this.showVinFilterModal = true;
      this.$nextTick(() => this.positionVinFilterPopover());
      this.addVinFilterClickOutside();
    },
    closeVinFilterModal() {
      this.showVinFilterModal = false;
      this.removeVinFilterClickOutside();
    },
    positionVinFilterPopover() {
      this.positionTextFilterPopover("vinFilterBtn", "vinFilterPopoverStyle", 240);
    },
    applyVinFilter() {
      this.filters.vin = (this.vinFilterTemp || "").trim();
      this.closeVinFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearVinFilter() {
      this.vinFilterTemp = "";
      this.filters.vin = "";
      this.closeVinFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addVinFilterClickOutside() {
      this.addTextFilterClickOutside("vinFilterPopover", "vinFilterBtn", "vinFilterClickOutside", () => this.closeVinFilterModal());
    },
    removeVinFilterClickOutside() {
      this.removeTextFilterClickOutside("vinFilterClickOutside");
    },
    toggleModelFilterPopover() {
      if (this.showModelFilterModal) this.closeModelFilterModal();
      else this.openModelFilterModal();
    },
    openModelFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeZipFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.modelFilterTemp = this.filters.model || "";
      this.showModelFilterModal = true;
      this.$nextTick(() => this.positionModelFilterPopover());
      this.addModelFilterClickOutside();
    },
    closeModelFilterModal() {
      this.showModelFilterModal = false;
      this.removeModelFilterClickOutside();
    },
    positionModelFilterPopover() {
      this.positionTextFilterPopover("modelFilterBtn", "modelFilterPopoverStyle", 260);
    },
    applyModelFilter() {
      this.filters.model = (this.modelFilterTemp || "").trim();
      this.closeModelFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearModelFilter() {
      this.modelFilterTemp = "";
      this.filters.model = "";
      this.closeModelFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addModelFilterClickOutside() {
      this.addTextFilterClickOutside("modelFilterPopover", "modelFilterBtn", "modelFilterClickOutside", () => this.closeModelFilterModal());
    },
    removeModelFilterClickOutside() {
      this.removeTextFilterClickOutside("modelFilterClickOutside");
    },
    positionTextFilterPopover(btnRef, styleKey, width = 280) {
      const btn = this.$refs[btnRef];
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      let left = rect.left;
      if (left + width > window.innerWidth) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      this[styleKey] = {
        position: "fixed",
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: "9999"
      };
    },
    addTextFilterClickOutside(popoverRef, btnRef, handlerKey, closeFn) {
      this.removeTextFilterClickOutside(handlerKey);
      setTimeout(() => {
        this[handlerKey] = (e) => {
          const popover = this.$refs[popoverRef];
          const btn = this.$refs[btnRef];
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          closeFn();
        };
        document.addEventListener("click", this[handlerKey]);
      }, 0);
    },
    removeTextFilterClickOutside(handlerKey) {
      if (this[handlerKey]) {
        document.removeEventListener("click", this[handlerKey]);
        this[handlerKey] = null;
      }
    },
    toggleZipFilterPopover() {
      if (this.showZipFilterModal) {
        this.closeZipFilterModal();
      } else {
        this.openZipFilterModal();
      }
    },
    openZipFilterModal() {
      this.closeCallerFilterModal();
      this.closeTargetNameFilterModal();
      this.closeCurrentInsuranceFilterModal();
      this.closeFirstNameFilterModal();
      this.closeLastNameFilterModal();
      this.closeDobFilterModal();
      this.closeEmailFilterModal();
      this.closeAddressFilterModal();
      this.closeYearFilterModal();
      this.closeMakeFilterModal();
      this.closeModelFilterModal();
      this.closeStateFilterModal();
      this.closeOwnOrRentFilterModal();
      this.closeVehiclesFilterModal();
      this.closeDispositionFilterModal();
      this.closeBillableStatusFilterModal();
      this.zipFilterTemp = this.filters.zip || "";
      this.showZipFilterModal = true;
      this.$nextTick(() => this.positionZipFilterPopover());
      this.addZipFilterClickOutside();
    },
    closeZipFilterModal() {
      this.showZipFilterModal = false;
      this.removeZipFilterClickOutside();
    },
    positionZipFilterPopover() {
      const btn = this.$refs.zipFilterBtn;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const gap = 4;
      const popoverWidth = 280;
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      this.zipFilterPopoverStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        zIndex: '9999'
      };
    },
    applyZipFilter() {
      this.filters.zip = (this.zipFilterTemp || "").trim();
      this.closeZipFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    clearZipFilter() {
      this.zipFilterTemp = "";
      this.filters.zip = "";
      this.closeZipFilterModal();
      this.currentPage = 1;
      this.fetchData();
    },
    addZipFilterClickOutside() {
      this.removeZipFilterClickOutside();
      setTimeout(() => {
        this.zipFilterClickOutside = (e) => {
          const popover = this.$refs.zipFilterPopover;
          const btn = this.$refs.zipFilterBtn;
          if (popover && popover.contains(e.target)) return;
          if (btn && btn.contains(e.target)) return;
          this.closeZipFilterModal();
        };
        document.addEventListener("click", this.zipFilterClickOutside);
      }, 0);
    },
    removeZipFilterClickOutside() {
      if (this.zipFilterClickOutside) {
        document.removeEventListener("click", this.zipFilterClickOutside);
        this.zipFilterClickOutside = null;
      }
    },
    clearFilters() {
      this.filters.inboundPhoneNumber = "";
      this.filters.targetNumber       = "";
      this.filters.targetName         = [];
      this.filters.currentinsurance   = [];
      this.filters.zip                = "";
      this.filters.firstname          = "";
      this.filters.lastname           = "";
      this.filters.dob                = "";
      this.filters.dob_from           = "";
      this.filters.dob_to             = "";
      this.filters.email              = "";
      this.filters.address            = "";
      this.filters.state              = [];
      this.filters.own_or_rent        = [];
      this.filters.number_of_vehicles = [];
      this.filters.year               = "";
      this.filters.make               = "";
      this.filters.model              = "";
      this.filters.insured_since      = "";
      this.filters.insurance_expiration = "";
      this.filters.vin                = "";
      this.filters.disposition        = [];
      this.filters.billable_status    = ["billable"];
      this.filters.lead_source        = [];
      this.filters.duration           = "";
      this.filters.connected_duration = "";
      this.filters.endCallSource      = "";
      this.dispositionFilterSelected = [];
      this.targetNameFilterSelected = [];
      this.billableStatusFilterSelected = ["billable"];
      this.leadSourceFilterSelected = [];
      this.currentPage = 1;
      this.fetchData();
    },
    /**
     * Same payload as POST /api/calllogs for a given page. Returns null if the current user
     * is not allowed to load call logs (e.g. agent with no transfer number).
     */
    buildCalllogsRequestData(page, pageSize) {
      const cleanFilters = {};
      Object.keys(this.filters).forEach((key) => {
        cleanFilters[key] =
          this.filters[key] === null || this.filters[key] === undefined
            ? ""
            : this.filters[key];
      });
      const formatLocalDate = (date) => {
        const pad = (n) => n.toString().padStart(2, "0");
        return (
          `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
          `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        );
      };

      if (!this.filters.date_from) {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        this.filters.date_from = formatLocalDate(todayStart);
      }

      if (!this.filters.date_to) {
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        this.filters.date_to = formatLocalDate(todayEnd);
      }

      const roless = Array.from(this.roles);
      if (!roless.includes("admin") && !roless.includes("manager")) {
        if (!this.transferNumbers.length && !this.transferNumber) {
          return null;
        }
      }

      const requestData = {
        reportStart: this.filters.date_from,
        reportEnd: this.filters.date_to,
        page,
        pageSize,
        filters: cleanFilters
      };
      if (!roless.includes("admin") && !roless.includes("manager")) {
        const nums = (this.transferNumbers && this.transferNumbers.length)
          ? this.transferNumbers
          : (this.transferNumber ? [this.transferNumber] : []);
        if (nums.length === 1) {
          requestData.targetNumber = nums[0];
        } else if (nums.length > 1) {
          requestData.targetNumbers = nums;
        }
      }
      return { requestData };
    },
    async fetchData() {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("Token is missing or expired.");
        this.$router.push('/login');
        return;
      }

      this.isLoading = true;
      await this.$nextTick();

      const built = this.buildCalllogsRequestData(this.currentPage, this.pageSize);
      if (built === null) {
        this.rows = [];
        this.allRows = [];
        this.totalItems = 0;
        this.totalPages = 1;
        this.currentPage = 1;
        this.isLoading = false;
        return;
      }
      const { requestData } = built;

      try {
        const res = await fetch(`/api/calllogs`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData)
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(`Error: ${res.status} - ${text}`);
          if (res.status === 401) {
            localStorage.removeItem('token');
            this.$router.push('/login');
          }
          return;
        }

        const data = await res.json();
        const mappedRows = (data.records || []).map(call => ({
          ...call,
          refundRequestCreated: call.refunds && call.refunds.length > 0,
          existingRefunds: call.refunds || []
        }));
        this.rows = mappedRows;
        this.allRows = mappedRows;
        this.totalItems = Number(data.total || mappedRows.length);
        this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));

        this.fetchWeeklyStats();
        return data;
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        this.isLoading = false;
      }
    },
    getWeekRangeFromDate(dateStr) {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(d);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      const pad = (n) => String(n).padStart(2, "0");
      return {
        reportStart: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())} 00:00:00`,
        reportEnd: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())} 23:59:59`
      };
    },
    async fetchWeeklyStats() {
      if (this.isAdmin || !this.userName) return;
      const token = localStorage.getItem("token");
      const range = this.getWeekRangeFromDate(this.filters.date_from);
      if (!range) return;

      try {
        const res = await fetch("/api/calllogs/weekly", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            reportStart: range.reportStart,
            reportEnd: range.reportEnd
          })
        });

        if (!res.ok) return;

        const data = await res.json();
        const records = data.records || [];
        const row = records[0] || null;

        if (row) {
          const completed = Number(row.count) || 0;
          const total = Number(row.total_points) || 0;
          const percent = total > 0 ? (completed / total) * 100 : 0;

          this.weeklyStats = {
            completed,
            total,
            percent,
            percentText: total > 0 ? (percent.toFixed(1) + "%") : "0%"
          };
        } else {
          this.weeklyStats = {
            completed: 0,
            total: 0,
            percent: 0,
            percentText: "0%"
          };
        }
      } catch (e) {
        console.error("Weekly stats fetch error:", e);
      }
    },
    async authorizedFetch(url, options = {}) {
      let token = localStorage.getItem("token");

      if (!options.headers) options.headers = {};
      options.headers["Authorization"] = `Bearer ${token}`;

      let response = await fetch(url, options);

      // Если токен истёк — пробуем рефреш
      if (response.status === 401) {
        const refresh_token = localStorage.getItem("refresh_token");
        if (!refresh_token) {
          this.logout();
          return;
        }

        // Запрос на рефреш токена
        const refreshRes = await fetch("/api/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token })
        });

        if (!refreshRes.ok) {
          // Если рефреш не прошёл — редирект на логин
          this.logout();
          return;
        }

        const data = await refreshRes.json();
        token = data.token;
        localStorage.setItem("token", token);

        // Повторяем исходный запрос с новым токеном
        options.headers["Authorization"] = `Bearer ${token}`;
        response = await fetch(url, options);
      }

      return response;
    },
    async downloadCSV() {
      if (this.isExportingCsv) return;

      try {
        this.isExportingCsv = true;
        const token = localStorage.getItem("token");
        if (!token) {
          this.$router.push("/login");
          return;
        }

        if (!Array.isArray(this.roles) || this.roles.length === 0) {
          await this.check_role();
        }
        if (!this.isAdmin && !this.isManager && !this.transferNumber) {
          await this.fetchTransferNumber();
        }

        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token
          },
          body: JSON.stringify({
            action_type: "download_csv billable report",
            username: localStorage.getItem("username")
          })
        });

        const built = this.buildCalllogsRequestData(1, this.pageSize);
        if (built === null) {
          alert("No data to export.");
          return;
        }

        // The CSV is generated and streamed server-side (see /api/calllogs/csv):
        // pulling a year via /api/calllogs exportAll into a Blob times out at
        // nginx and holds hundreds of MB in the tab. We POST the same request
        // body to get a short-lived download token, then hand the URL to the
        // browser's download manager which streams it straight to disk.
        const { requestData } = built;
        const prep = await fetch(`/api/calllogs/csv/prepare`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData)
        });
        if (prep.status === 401) {
          localStorage.removeItem("token");
          this.$router.push("/login");
          return;
        }
        if (!prep.ok) {
          const body = await prep.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${prep.status}`);
        }
        const { download_token } = await prep.json();
        if (!download_token) throw new Error("No download token received");

        const link = document.createElement("a");
        link.href = `/api/calllogs/csv?dl=${encodeURIComponent(download_token)}`;
        link.download = `billable_report_${this.filters.date_from?.slice(0, 10) || "export"}.csv`;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (e) {
        console.error("CSV export error:", e);
        alert(`Failed to download CSV: ${e.message || e}`);
      } finally {
        // The browser owns the download from here; we only know it started.
        setTimeout(() => { this.isExportingCsv = false; }, 1500);
      }
    }
  },
  watch: {
    'filters.date_from'() {}, // fetchData вызывается через onDateRangeChange / applyQuickRange
    'filters.date_to'() {},
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
      }
    },
  }
};
</script>

<style scoped>

#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 10000;
}

.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.loading-spinner {
  width: 52px;
  height: 52px;
  border: 4px solid rgba(78, 84, 200, 0.18);
  border-top-color: #4e54c8;
  border-radius: 50%;
  animation: ringba-spin 0.8s linear infinite;
}

.loading-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

@keyframes ringba-spin {
  to {
    transform: rotate(360deg);
  }
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.panel-action-btn {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 10px 20px;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 150px;
  max-width: 150px;
  height: 106.59px;
  flex-shrink: 0;
  margin-left: auto;
}

.panel-action-btn:hover {
  transform: translateY(-2px);
}

.panel-action-btn:active {
  transform: translateY(1px);
}

.btn-submit {
  width: 200px;
  max-width: 100%;
  display: inline-block;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease, transform 0.2s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn-submit:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.btn-submit:active {
  transform: translateY(1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.summary-panels-wrapper {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-panels-container {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 110px;
  background: #f0f4ff;
  padding: 10px 15px;
  border-radius: 8px;
  text-align: center;
  flex: 1 1 150px;
  min-width: 150px;
  max-width: 150px;
}

.panel-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
}

.panel-value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}
.v-select {
  font-size: 12px;
  border-radius: 8px;
}

.v-select .vs__dropdown-toggle {
  min-height: 32px;
}

.v-select .vs__selected {
  font-size: 12px;
}


.table-responsive table {
  min-width: 700px;
  -webkit-overflow-scrolling: touch;
  table-layout: fixed;
  width: 100%
}

table {
  width: 100%;
  table-layout: auto;
  font-size: 12px;
}

th, td {
  word-wrap: break-word;
  vertical-align: middle !important;
}

.filter-input {
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 100%;
}
.modal-overday {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }
.multiselect-sm {
  font-size: 14px;
  min-height: 32px;
}

/* Create-refund modal */
.modal-create-content {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.2s ease;
}

@media (max-width: 768px) {
  .modal-create-content {
    width: 95%;
    max-width: 95%;
    padding: 16px;
    max-height: 85vh;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .transfer-numbers-wrapper {
    max-height: 200px;
  }

  .form-field input,
  .form-field .vs__dropdown-toggle {
    font-size: 14px;
  }
}


.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 300;
}

.btn-primary {
  background: #4f46e5;
  color: white;
  font-size: 12px;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-responsive table {
  min-width: 1000px;
  width: 100%;
  table-layout: auto;
}

@media (max-width: 768px) {
  .table-responsive table {
    min-width: 700px;
  }
  th, td {
    white-space: nowrap;
  }
}
@media (max-width: 768px) {
  .login-title { font-size: 22px; }
  table { font-size: 10px; }
  .filter-input { font-size: 11px; padding: 4px 6px; }
  .panel-action-btn { max-width: 100%; padding: 8px 16px; height: 110px; }
  .summary-panels-container { gap: 10px; }
}

.large-textarea {
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border-radius: 8px;
  resize: vertical;
  font-size: 14px;
}
.btn-secondary {
  background: #e5e7eb;
  color: #111;
}

.btn-secondary:hover {
  background: #d1d5db;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-actions .btn {
  min-width: 100px;
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.billable-status-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.billable-reason-chip {
  width: auto;
  min-width: 0;
  max-width: 150px;
  cursor: pointer;
  appearance: none;
  text-align: left;
}

.billable-action-link {
  color: #4f46e5;
  font-size: 11px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.billable-action-link:hover {
  color: #3730a3;
  text-decoration: underline;
}

.billable-watermark {
  max-width: 150px;
  font-size: 10px;
  line-height: 1.25;
  color: #6b7280;
  text-align: center;
  word-break: break-word;
}

.billable-status-modal {
  max-width: 520px;
}

.billable-status-modal-body {
  display: grid;
  gap: 8px;
  color: #374151;
  font-size: 14px;
}

.billable-reason-label {
  display: grid;
  gap: 6px;
}

.billable-reason-label .form-select,
.billable-reason-label .form-control {
  width: 100%;
}

.billable-reason-other {
  min-height: 96px;
  resize: vertical;
}


.grafana-sidebar {
  display: flex;
  flex-direction: column;
  width: 150px;
  padding: 8px 6px;
  background: #f5f5fa;
  border-right: 1px solid #2d2d32;
  overflow-y: auto;
  max-height: 320px;
  gap: 2px;
}

.grafana-sidebar button {
  background: none;
  border: none;
  color: #000000;
  font-size: 12px;
  padding: 6px 10px;
  text-align: left;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.grafana-sidebar button:hover {
  background: #2d2d32;
  color: #ffffff; 
}

.grafana-sidebar-divider {
  height: 1px;
  background: #2d2d32;
  margin: 4px 0;
}
.grafana-datepicker {
  width: 300px !important;
}
.grafana-datepicker {
  font-size: 11px;
}

.grafana-datepicker .dp__menu {
  font-size: 11px !important;
}

.grafana-datepicker .datepicker,
.grafana-datepicker .datepicker * {
  font-size: 11px !important;
}
.grafana-datepicker .datepicker__input {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__menu) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__calendar) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__cell_inner) {
  font-size: 11px !important;
}

.grafana-datepicker :deep(.dp__input) {
  font-size: 11px !important;
  height: 32px !important;
  padding: 4px 8px 4px 35px !important;
}
.coins-action {
  color: #999999;
  text-decoration: none;
}
.coins-action:hover {
  color: #f59e0b;
  transform: none;
}

.transfers-stats-panel {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
}

.transfers-stats-card {
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%);
  border: 1px solid rgba(78, 84, 200, 0.2);
  border-radius: 12px;
  padding: 16px 24px;
  min-width: 280px;
  box-shadow: 0 4px 12px rgba(78, 84, 200, 0.08);
}

.transfers-stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.transfers-stats-title {
  font-size: 13px;
  font-weight: 600;
  color: #4e54c8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.transfers-stats-week {
  font-size: 11px;
  color: #6b7280;
}

.transfers-stats-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.transfers-stats-main {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.transfers-stats-value {
  font-size: 28px;
  font-weight: 800;
  color: #4e54c8;
  line-height: 1;
}

.transfers-stats-sep {
  font-size: 22px;
  font-weight: 600;
  color: #9ca3af;
}

.transfers-stats-total {
  font-size: 22px;
  font-weight: 700;
  color: #374151;
}

.transfers-stats-unit {
  font-size: 13px;
  color: #6b7280;
  margin-left: 4px;
}

.transfers-stats-progress-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.transfers-stats-progress-bar {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.transfers-stats-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4e54c8, #2575fc);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.transfers-stats-percent {
  font-size: 14px;
  font-weight: 700;
  color: #4e54c8;
  min-width: 42px;
}

.table-top-filters {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 0px;
  margin-top: 10px;
}

.table-top-filters {
  position: relative;
  overflow: visible;
}
.refund-reason-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: 150px;
  min-width: 150px;
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.15s;
  line-height: 1.4;
}

.refund-reason-chip:hover {
  opacity: 0.8;
}

.refund-chip-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.refund-chip-status-dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.refund-chip-pending {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}
.refund-chip-pending .refund-chip-status-dot {
  background: #9ca3af;
}

.refund-chip-approved {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}
.refund-chip-approved .refund-chip-status-dot {
  background: #16a34a;
}

.refund-chip-rejected {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}
.refund-chip-rejected .refund-chip-status-dot {
  background: #dc2626;
}

.link-csv {
  font-size: 12px;
  color: #4f46e5;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.link-csv:hover {
  text-decoration: underline;
}

.link-csv--disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: wait;
}

/* Caller filter — same style as Home.vue phone filter */
.campaign-header-cell {
  vertical-align: bottom !important;
}
.campaign-header-cell-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.campaign-header-cell span {
  font-weight: 600;
}
.filter-icon-btn {
  position: relative;
  width: auto;
  height: auto;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}
.filter-icon-btn:hover {
  color: #4e54c8;
}
.filter-icon-btn.active {
  color: #4e54c8;
}
.filter-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: #4e54c8;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
/* Base popover (Campaign-style: list + search) — as in Home.vue */
.filter-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 320px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}
.caller-filter-popover.filter-popover {
  width: 280px;
  max-height: none;
}
.filter-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}
.filter-popover-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}
.filter-popover-close {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  font-size: 18px;
  color: #888;
  cursor: pointer;
  line-height: 1;
}
.filter-popover-close:hover {
  color: #333;
}
.filter-popover-body {
  padding: 10px 12px;
  overflow-y: auto;
  flex: 1;
}
.filter-popover-hint {
  margin-top: 8px;
  font-size: 11px;
  line-height: 1.35;
  color: #64748b;
}
.filter-popover-search {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 8px;
}
.filter-popover-search:focus {
  outline: none;
  border-color: #4e54c8;
}
.dob-filter-body .dob-filter-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin: 4px 0 2px;
}
.dob-filter-body .dob-filter-label:first-child {
  margin-top: 0;
}
.dob-filter-body .filter-popover-search {
  margin-bottom: 4px;
}
.filter-popover-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}
.filter-popover-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  margin: 0;
  font-size: 12px;
}
.filter-popover-item:last-child {
  border-bottom: none;
}
.filter-popover-item:hover {
  background: #f8f9ff;
}
.filter-popover-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
}
.filter-popover-empty {
  padding: 16px;
  text-align: center;
  color: #888;
  font-size: 12px;
}
.filter-popover-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}

#reportPage table {
  font-size: 10px;
}

.table thead {
  font-size: 10px;
  font-weight: 600;
}

.table thead th {
  vertical-align: bottom !important;
}

.table tbody {
  font-size: 10px;
}

/* Для фильтров (input в шапке таблицы) */
.filter-input {
  font-size: 10px;
  padding: 2px 4px;
}
.audio-btn {
  width: 18px;
  height: 18px;
  min-width: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.audio-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.download-btn {
  background-color: #28a745;
  color: white;
}

.play-btn {
  background-color: #007bff;
  color: white;
}
.tooltip-container {
  position: relative;
  display: inline-block;
  cursor: default;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 4px 8px;
  position: absolute;
  z-index: 10;
  white-space: nowrap;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.tooltip-container .tooltip-text.right {
  top: 50%;
  left: 100%;
  transform: translate(8px, -50%);
}

.tooltip-container {
  position: relative;
  display: inline-block;
  cursor: default;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 4px 8px;
  position: absolute;
  z-index: 10;
  white-space: nowrap;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.tooltip-container .tooltip-text.right {
  top: 50%;
  left: 100%;
  transform: translate(8px, -50%);
}
</style>